import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api, SERVER_ORIGIN } from '../services/api';
import { textsPath } from '../utils/textsLink';
import { byRecentAssignment, fmtLastWorked } from '../utils/people';
import { ArrowLeft, MapPin, User, Calendar, CalendarClock, DollarSign, Clock, Send, Check, CheckCircle, X, Edit2, Camera, MessageSquare, Activity, Plus, Trash2, AlertTriangle, Wand2, RefreshCw, Leaf, Gift } from 'lucide-react';
import { toast } from 'react-toastify';
import { copyOpenShiftLink } from '../utils/openShiftLink';
import { fmtTs, fmtLocalDate, fmtWindow, businessToday } from '../utils/time';
import { money } from '../utils/format';
import CrewNoteEditor from '../components/CrewNoteEditor';
import PeoplePicker from '../components/PeoplePicker';
import OpenShiftDrafts, { ShiftDraft } from '../components/OpenShiftDrafts';
import GiveShiftTo from '../components/GiveShiftTo';
import PeopleNeededInput from '../components/PeopleNeededInput';
import { fetchOfferJobs, offerJobsKey, jobsWord, allJobsWord, shortDay } from '../utils/openShiftDay';
import { NotePhotoStrip, AddNotePhotoButton } from '../components/NotePhotos';
import { useReadOnly } from '../hooks/useReadOnly';
import StandingAddonRules, { ruleBody } from '../components/StandingAddonRules';

/** Hours on a to-do that pay the people on it (POST /jobs/:id/paid-time). 0 clears it. */
function PaidTimeControl({ job, people, readOnly, onSaved }: { job: any; people: string[]; readOnly: boolean; onSaved: () => void }) {
  const current = Number(job.estimated_minutes_snapshot) || 0;
  const [hours, setHours] = useState<string>(current ? String(Math.round((current / 60) * 100) / 100) : '');
  const [done, setDone] = useState<boolean>(job.status === 'complete');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setHours(current ? String(Math.round((current / 60) * 100) / 100) : ''); setDone(job.status === 'complete'); }, [current, job.status]);
  async function save() {
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${job.id}/paid-time`, { paid_hours: Number(hours) || 0, ...(job.status === 'complete' ? {} : { done }) });
      toast.success(data?.message || 'Saved');
      onSaved();
    } catch (e: any) { toast.error(e?.response?.data?.message || 'Could not set the paid time'); }
    finally { setBusy(false); }
  }
  const first = people.map(p => String(p).split(' ')[0]);
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <input type="number" inputMode="decimal" min={0} step={0.25} value={hours} onChange={e => setHours(e.target.value)} placeholder="0" className="input w-24 py-1.5" />
        <span className="text-sm text-gray-600">hours</span>
        {job.status !== 'complete' && (
          <label className="flex items-center gap-1.5 text-sm text-gray-700"><input type="checkbox" checked={done} onChange={e => setDone(e.target.checked)} className="rounded border-gray-300 text-brand-600" /> done, pay now</label>
        )}
        <button onClick={save} disabled={readOnly || busy} className="btn-primary text-xs py-1.5 px-3">{busy ? 'Saving…' : 'Save'}</button>
      </div>
      <p className="text-[11px] text-gray-500">
        {current ? `${Math.round((current / 60) * 100) / 100} h pays ${first.length ? first.join(', ') : 'nobody yet — pick people above'} at their rate${job.status === 'complete' ? ', on this day’s pay' : ' once it is done'}.` : 'None. Hours here pay the people on this to-do at their hourly rate, like a mow. 0 clears it.'}
      </p>
    </div>
  );
}

const statusBadge: Record<string, string> = {
  scheduled: 'badge-scheduled', in_progress: 'badge-in_progress',
  complete: 'badge-complete',   skipped: 'badge-skipped',
};
const API_BASE = SERVER_ORIGIN;

export default function JobDetail() {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  // Office-side controls that were missing: cancel a scheduled visit (it shows as
  // skipped for the crew and on the customer's page) or delete the job for good.
  const cancelVisit = useMutation((reason: string) => api.post(`/jobs/${id}/skip`, { reason }), {
    onSuccess: () => { toast.success('Visit cancelled'); qc.invalidateQueries(['job', id]); qc.invalidateQueries('jobs-list'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not cancel that visit'); },
  });
  const deleteJob = useMutation(() => api.delete(`/jobs/${id}`), {
    onSuccess: () => { toast.success('Job deleted'); qc.invalidateQueries('jobs-list'); navigate('/jobs'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not delete that job'); },
  });
  // A free visit, on us (server/services/freeVisit): the invoice shows the
  // normal price and a "Free mow — on us" line taking it to $0. Crew pay is
  // untouched. The reason is for the office only.
  const makeFree = useMutation((reason: string) => api.post(`/jobs/${id}/free`, { reason }).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'This visit is free'); qc.invalidateQueries(['job', id]); qc.invalidateQueries('jobs-list'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not make this visit free'); },
  });
  const undoFree = useMutation(() => api.delete(`/jobs/${id}/free`).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Charging for this visit again'); qc.invalidateQueries(['job', id]); qc.invalidateQueries('jobs-list'); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not undo that'); },
  });
  // "Don't bill" (server/services/noBill — owner, Oct 1 2026): this visit is not owed, so month-end leaves
  // it alone. Set from Billing → Unbilled or a voided bill; undone here, and it waits to be billed again.
  const undoNoBill = useMutation(() => api.delete(`/jobs/${id}/no-bill`).then(r => r.data), {
    onSuccess: (d: any) => {
      toast.success(d?.message || 'Back to waiting to be billed');
      qc.invalidateQueries(['job', id]); qc.invalidateQueries('jobs-list'); qc.invalidateQueries('billing-unbilled'); qc.invalidateQueries('billing-dashboard');
    },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not undo that'); },
  });
  const [repeatOpen, setRepeatOpen] = useState(false);
  const [timeOpen, setTimeOpen] = useState(false);
  const [spanOpen, setSpanOpen] = useState(false);
  const [priceOpen, setPriceOpen] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);
  const [editForm, setEditForm] = useState<any>(null);
  const [quickMsg, setQuickMsg] = useState<{ type: string; preview: any } | null>(null);
  const [quickLoading, setQuickLoading] = useState<string | null>(null);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);

  const [addingLineItem, setAddingLineItem] = useState(false);
  const [lineItemForm, setLineItemForm] = useState({ description: '', quantity: '1', unit_price: '' });
  const [aiOpen, setAiOpen] = useState(false);
  const [aiText, setAiText] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiQuote, setAiQuote] = useState('');
  const [crewEditOpen, setCrewEditOpen] = useState(false);
  const [crewPick, setCrewPick] = useState<Set<number>>(new Set());
  const [editingLineItem, setEditingLineItem] = useState<any | null>(null);

  const { data, isLoading, error } = useQuery(['job', id], () => api.get(`/jobs/${id}`).then(r => r.data));
  const { data: membersData } = useQuery('crew-members-all', () => api.get('/crews/members/all').then(r => r.data.members as any[]));
  const { data: msgPreview } = useQuery(['msg-preview', id], () =>
    api.get(`/messages/job/${id}/preview`).then(r => r.data.preview),
    { enabled: data?.job?.status === 'complete', retry: false }
  );
  const { data: locationData } = useQuery(['job-location-events', id], () =>
    api.get(`/jobs/${id}/location-events`).then(r => r.data),
    { staleTime: 30000 }
  );
  // Quotes for this visit's customer — see and start quotes from the job.
  const { data: jobQuotes } = useQuery(
    ['job-quotes', data?.job?.customer_id],
    () => api.get('/quotes', { params: { customer_id: data.job.customer_id } }).then(r => r.data.quotes as any[]),
    { enabled: !!data?.job?.customer_id }
  );
  const locationEvents: any[] = locationData?.events || [];

  // One tap on the page itself, no Edit form (owner, Sep 18 2026).
  const officeComplete = useMutation(async () => api.post(`/jobs/${id}/office-complete`, {}).then(r => r.data), {
    onSuccess: (d: any) => { qc.invalidateQueries(['job', id]); qc.invalidateQueries('jobs'); qc.invalidateQueries('crew-pay'); toast.success(d?.message || 'Marked complete'); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not mark it complete'); },
  });
  const updateJob = useMutation(async (updates: any) => (await api.put(`/jobs/${id}`, updates)).data, {
    onSuccess: (d: any) => { qc.invalidateQueries(['job', id]); toast.success(d?.message || 'Job updated'); setEditOpen(false); },
    onError: (e: any) => { toast.error(e?.response?.data?.message ? `Couldn't save — ${e.response.data.message}` : 'Failed to update job'); },
  });

  const markSent = useMutation(async () => {
    if (!msgPreview) return;
    await api.post(`/messages/job/${id}/sent`, { channel: 'manual', recipient: msgPreview.to, subject: msgPreview.subject, body: msgPreview.body });
  }, {
    onSuccess: () => { qc.invalidateQueries(['job', id]); toast.success('Marked as sent'); setMessageOpen(false); },
    onError: () => { toast.error('Failed to record'); },
  });

  const sendQuick = useMutation(async ({ type, body, recipient }: { type: string; body: string; recipient: string }) => {
    await api.post('/messages/send', {
      customer_id: data?.job?.customer_id,
      job_id: Number(id),
      message_type: type,
      channel: 'manual',
      recipient,
      body,
    });
  }, {
    onSuccess: () => { toast.success('Message recorded — copy and send manually'); setQuickMsg(null); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Failed to send'); },
  });

  async function openQuick(type: string) {
    if (!data?.job) return;
    setQuickLoading(type);
    try {
      const r = await api.post('/messages/preview', {
        customer_id: data.job.customer_id,
        job_id: Number(id),
        message_type: type,
      });
      setQuickMsg({ type, preview: r.data.preview });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Cannot preview message');
    } finally {
      setQuickLoading(null);
    }
  }

  const addLineItem = useMutation(async () => {
    await api.post(`/jobs/${id}/line-items`, {
      description: lineItemForm.description,
      quantity: Number(lineItemForm.quantity) || 1,
      unit_price: Number(lineItemForm.unit_price),
    });
  }, {
    onSuccess: () => {
      qc.invalidateQueries(['job', id]);
      setAddingLineItem(false);
      setLineItemForm({ description: '', quantity: '1', unit_price: '' });
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Failed to add item'); },
  });

  const updateLineItem = useMutation(async (item: any) => {
    await api.put(`/jobs/${id}/line-items/${item.id}`, {
      description: item.description,
      quantity: Number(item.quantity),
      unit_price: Number(item.unit_price),
    });
  }, {
    onSuccess: () => { qc.invalidateQueries(['job', id]); setEditingLineItem(null); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Failed to update item'); },
  });

  const deleteLineItem = useMutation(async (liId: number) => {
    await api.delete(`/jobs/${id}/line-items/${liId}`);
  }, {
    onSuccess: () => { qc.invalidateQueries(['job', id]); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Failed to delete item'); },
  });

  // Who's on this job — job_crew override; the way one-time work gets staffed
  const { data: jobCrewData, refetch: refetchJobCrew } = useQuery(
    ['job-crew', id],
    () => api.get(`/jobs/${id}/crew`).then(r => r.data),
  );
  // A visit already on a bill can be corrected — that is how its pay and tip follow who really worked it.
  // When pay for it already went out to someone coming off it, the server asks first (JOB_PAY_SENT).
  const saveJobCrew = useMutation(async ({ ids, confirmPaid }: { ids: number[]; confirmPaid?: boolean }) =>
    (await api.put(`/jobs/${id}/crew`, { crew_member_ids: ids, ...(confirmPaid ? { confirm_paid: true } : {}) })).data, {
    onSuccess: (d: any, { ids }) => {
      toast.success(d?.tips_reshared ? d.message : ids.length ? `${ids.length} assigned — they'll see it on their phones` : 'Assignment cleared');
      for (const w of d?.tip_warnings || []) toast.warn(w, { autoClose: false });
      setCrewEditOpen(false);
      refetchJobCrew(); qc.invalidateQueries(['job', id]);
    },
    onError: (e: any, vars) => {
      const r = e?.response?.data;
      if (r?.code === 'JOB_PAY_SENT' && !vars.confirmPaid) {
        if (window.confirm(r.message)) saveJobCrew.mutate({ ...vars, confirmPaid: true });
        return;
      }
      toast.error(r?.message || 'Failed to assign');
    },
  });

  // Subcontracting: mark the job done by an outside sub; their cost hits profit.
  const [subOpen, setSubOpen] = useState(false);
  const [subName, setSubName] = useState('');
  const [subCost, setSubCost] = useState('');
  const subcontract = useMutation(
    (clear?: boolean) => api.post(`/jobs/${id}/subcontract`, clear
      ? { subcontractor_name: null, subcontractor_cost: null }
      : { subcontractor_name: subName.trim(), subcontractor_cost: subCost ? Number(subCost) : null }).then(r => r.data),
    {
      onSuccess: (r: any) => { toast.success(r?.message || 'Saved'); setSubOpen(false); qc.invalidateQueries(['job', id]); qc.invalidateQueries('profit'); },
      onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not save'); },
    });

  // Paste raw walkthrough notes → AI builds the itemized quote: billable rows
  // land as line items here, and the formatted customer text is copy-ready.
  // Optional/either-or rows are NOT auto-billed — they live in the quote text
  // until the customer picks one.
  async function buildAiQuote() {
    const raw = aiText.trim();
    if (!raw) return;
    setAiBusy(true);
    try {
      const r = await api.post('/ai/itemize-quote', { text: raw, service_type: data?.job?.service_type });
      const rows = (r.data.line_items || []).filter((li: any) =>
        li.description && li.unit_price != null && !li.optional);
      for (const li of rows) {
        await api.post(`/jobs/${id}/line-items`, {
          description: `${li.section != null ? `${li.section}. ` : ''}${li.description}${li.free ? ' (FREE)' : ''}`,
          quantity: Number(li.quantity) || 1,
          unit_price: Number(li.unit_price) || 0,
        });
      }
      setAiQuote(r.data.customer_text || '');
      setAiText('');
      qc.invalidateQueries(['job', id]);
      toast.success(`${rows.length} line items added — quote text ready to copy`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'AI quote failed — try again');
    } finally { setAiBusy(false); }
  }

  // Pay page "Adjust pay" lands here with ?edit=pay → open the editor on the pay fields.
  useEffect(() => {
    if (!data?.job || editOpen) return;
    if (window.location.search.includes('edit=pay')) {
      window.history.replaceState({}, '', window.location.pathname);
      openEdit();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.job?.id]);

  if (isLoading) return <div className="py-12 text-center text-gray-500">Loading…</div>;
  if (error || !data?.job) return (
    <div className="py-12 text-center">
      <p className="text-red-600 mb-4">Failed to load job.</p>
      <button onClick={() => navigate(-1)} className="text-brand-600 hover:text-brand-700">← Go back</button>
    </div>
  );

  const job = data.job;
  if (job.service_type === 'estimate') return <Navigate to={`/quote-visits/${id}`} replace />;
  const photos: any[] = data.photos || [];
  const lineItems: any[] = data.lineItems || [];
  const members = membersData || [];
  const effectivePay = job.crew_pay_is_manual ? job.crew_pay_override : job.estimated_crew_pay;
  // A stop still to do on a day offered as one open shift (several jobs, one claim): its people are whoever claims the day.
  const offeredDayStop = Number(job.route_open_shift) === 1 && ['scheduled', 'in_progress'].includes(String(job.status || ''));
  // A to-do has no yard: its address is its own (jobs.stop_address).
  const isTodo = job.service_type === 'todo';
  const fullAddress = isTodo ? (job.stop_address || '') : [job.address, job.city, job.state, job.zip_code].filter(Boolean).join(', ');

  function openEdit() {
    setEditForm({
      customer_id: job.customer_id, property_id: job.property_id || '',
      crew_id: job.crew_id || '', assigned_crew_member_id: job.assigned_crew_member_id || '',
      scheduled_date: job.scheduled_date, time_start: job.time_start || '',
      time_end: job.time_end || '', notes: job.notes || '', status: job.status,
      // No is_open_shift here: the form has no control for it, and sending the
      // copy back could reopen a shift that filled while the form was open.
      // Open shift changes only through its own panel.
      time_is_firm: !!job.time_is_firm,
      price_snapshot: job.price_snapshot ?? '', estimated_minutes_snapshot: job.estimated_minutes_snapshot ?? '',
      actual_minutes: job.actual_minutes ?? '',
      crew_pay_override: job.crew_pay_override ?? '', crew_pay_is_manual: !!job.crew_pay_is_manual,
      spray_needed: !!job.spray_needed, spray_notes: job.spray_notes || '',
      ...(job.service_type === 'todo' ? { stop_address: job.stop_address || '' } : {}),
    });
    setEditOpen(true);
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3">
        <button onClick={() => navigate(-1)} className="mt-1 text-gray-500 hover:text-gray-600"><ArrowLeft className="h-5 w-5" /></button>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-black uppercase tracking-tight text-gray-900">{job.customer_name || job.address}</h1>
            <span className={statusBadge[job.status] || 'badge-scheduled'}>{job.status.replace('_', ' ')}</span>
            {job.is_first_mow && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-sky-800 bg-sky-100 rounded-full px-2 py-0.5" title="No finished mow at this yard yet — the crew are asked to add notes for next time">
                First mow
              </span>
            )}
            {job.comped_at && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-800 bg-emerald-100 rounded-full px-2 py-0.5">
                <Gift className="h-3 w-3" /> Free — on us
              </span>
            )}
            {job.no_bill_at && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-900 bg-amber-100 rounded-full px-2 py-0.5">
                Not billed
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-0.5">
            {job.property_label && !/^main property$/i.test(job.property_label) ? `${job.property_label} · ` : ''}{job.address?.split(',')[0] || ''}
            {/* What gets done where, and the locks (components/SiteMapEditor). */}
            {job.property_id && <> · <Link to={`/site-map/${job.property_id}`} className="text-brand-600 underline">Site map</Link></>}
            {/* Text them on Texts (the business number), or call — the office only; crew never get the number. */}
            {job.customer_phone && !isTodo && <> · <Link to={textsPath(job.customer_phone)} className="text-brand-600 underline">Text</Link> · <a href={`tel:${String(job.customer_phone).replace(/[^\d+]/g, '')}`} className="text-brand-600 underline">Call</a></>}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {['scheduled', 'in_progress'].includes(job.status) && (
            <button
              onClick={() => { if (window.confirm(`Mark ${job.customer_name || 'this job'} complete?${job.actual_minutes > 0 ? '' : ' No clock was run, so the estimate stands in for the time.'} Pay is figured for whoever is on it.`)) officeComplete.mutate(); }}
              disabled={readOnly || (officeComplete.isLoading)}
              className="flex items-center gap-1.5 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 rounded-lg px-3 py-1.5 disabled:opacity-50">
              <CheckCircle className="h-4 w-4" /> {officeComplete.isLoading ? 'Marking…' : 'Complete job'}
            </button>
          )}
          {!['complete', 'in_progress'].includes(job.status) && (
            <button
              onClick={() => setRescheduleOpen(true)}
              className="flex items-center gap-1 text-sm text-amber-700 hover:text-amber-800 font-medium"
            >
              <CalendarClock className="h-4 w-4" /> Reschedule
            </button>
          )}
          <button onClick={openEdit} className="flex items-center gap-1 text-sm text-brand-600 hover:text-brand-700 font-medium">
            <Edit2 className="h-4 w-4" /> Edit
          </button>
          {!job.comped_at && !job.no_bill_at && !['skipped', 'canceled', 'cancelled'].includes(job.status) && Number(job.price_snapshot) > 0 && (
            <button
              onClick={() => {
                const reason = window.prompt(`Make this visit free — $${Number(job.price_snapshot).toFixed(2)} on us. Why? (office only; the customer's invoice just says "on us")`, '');
                if (reason !== null) makeFree.mutate(reason.trim());
              }}
              disabled={makeFree.isLoading}
              className="flex items-center gap-1 text-sm text-emerald-700 hover:text-emerald-800 font-medium disabled:opacity-50">
              <Gift className="h-4 w-4" /> {makeFree.isLoading ? 'Saving…' : 'Free visit'}
            </button>
          )}
          {job.status === 'scheduled' && (
            <button onClick={() => { const reason = window.prompt('Cancel this visit — why? (the crew and the customer\'s page will show it as skipped)', 'Cancelled by office'); if (reason && reason.trim()) cancelVisit.mutate(reason.trim()); }}
              disabled={readOnly || (cancelVisit.isLoading)} className="flex items-center gap-1 text-sm text-gray-600 hover:text-red-700 font-medium disabled:opacity-50">
              <X className="h-4 w-4" /> Cancel visit
            </button>
          )}
          {job.status !== 'in_progress' && (
            <button onClick={() => { if (window.confirm('Delete this job for good? Its photos and route position go with it. Invoices already sent are not touched.')) deleteJob.mutate(); }}
              disabled={readOnly || (deleteJob.isLoading)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-red-700 font-medium disabled:opacity-50">
              <Trash2 className="h-4 w-4" /> Delete
            </button>
          )}
        </div>
      </div>

      {/* Open shift: who it's offered to, who claimed, texting them — and
          changing any of it while the job is still scheduled. */}
      <OpenShiftPanel key={String(id)} job={job} jobId={String(id)} readOnly={readOnly} />

      {/* Quotes for this customer — see and start quotes right from the visit. */}
      {job.customer_id && (
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900">Quotes</p>
            <button
              onClick={() => navigate('/quotes', { state: { prefillCustomerId: job.customer_id } })}
              className="text-xs font-medium text-brand-700 hover:text-brand-900">+ New quote</button>
          </div>
          {(jobQuotes || []).length === 0 ? (
            <p className="text-xs text-gray-500 mt-1">No quotes for {job.customer_name || 'this customer'} yet.</p>
          ) : (
            <ul className="mt-1 divide-y divide-gray-50">
              {jobQuotes!.map((q: any) => (
                <li key={q.id}>
                  <Link to="/quotes" className="flex items-center justify-between py-1.5 text-sm text-gray-700 hover:text-brand-700">
                    <span className="truncate">{q.title}</span>
                    <span className="text-xs text-gray-500 flex-shrink-0 ml-2">{q.status} · ${Number(q.total || 0).toFixed(0)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Subcontracting: mark the job done by an outside sub; their cost hits profit. */}
      <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-900">Subcontracted</p>
            {job.subcontractor_name
              ? <p className="text-xs text-gray-600">{job.subcontractor_name}{job.subcontractor_cost != null ? ` · $${Number(job.subcontractor_cost).toFixed(0)} cost` : ''}</p>
              : <p className="text-xs text-gray-500">Done by the crew.</p>}
          </div>
          <button
            onClick={() => { setSubName(job.subcontractor_name || ''); setSubCost(job.subcontractor_cost != null ? String(job.subcontractor_cost) : ''); setSubOpen(o => !o); }}
            className="text-xs font-medium text-brand-700 hover:text-brand-900">
            {subOpen ? 'Cancel' : (job.subcontractor_name ? 'Edit' : 'Mark subcontracted')}
          </button>
        </div>
        {subOpen && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <input value={subName} onChange={e => setSubName(e.target.value)} placeholder="Subcontractor name"
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-48" />
            <input value={subCost} onChange={e => setSubCost(e.target.value.replace(/[^0-9.]/g, ''))} placeholder="Cost $" inputMode="decimal"
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-24" />
            <button onClick={() => subcontract.mutate(false)} disabled={readOnly || (subcontract.isLoading || !subName.trim())}
              className="press text-xs px-3 py-1.5 rounded-xl bg-brand-600 text-black font-semibold disabled:opacity-50">Save</button>
            {job.subcontractor_name && (
              <button onClick={() => subcontract.mutate(true)} disabled={readOnly || (subcontract.isLoading)}
                className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600">Clear</button>
            )}
          </div>
        )}
      </div>

      {/* Multi-day work: one run of linked day jobs. Day 1 carries the price;
          every day has its own clock and pay. */}
      {(job.run_days || []).length > 1 ? (
        <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="font-semibold">Day {job.span_day} of {job.run_days.length}</span>
            <span className="text-indigo-700 text-xs">
              {job.price_split === 'days'
                ? `each day bills its own share · $${runTotal(job).toFixed(2)} in all · each day starts and finishes on its own`
                : 'billed once on day 1 · each day starts and finishes on its own'}
            </span>
            {!['complete', 'in_progress'].includes(job.status) && (
              <button onClick={() => setSpanOpen(true)} className="ml-auto text-xs font-semibold underline">Add days</button>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {job.run_days.map((d: any, i: number) => (
              <Link key={d.id} to={`/jobs/${d.id}`}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border ${d.id === job.id ? 'bg-indigo-600 text-white border-indigo-600' : d.status === 'complete' ? 'bg-green-50 text-green-800 border-green-200' : d.status === 'in_progress' ? 'bg-yellow-50 text-yellow-800 border-yellow-200' : 'bg-white text-indigo-800 border-indigo-200'}`}>
                Day {i + 1} · {fmtLocalDate(d.scheduled_date, { weekday: 'short', month: 'short', day: 'numeric' })}{d.actual_minutes ? ` · ${d.actual_minutes}m` : d.estimated_minutes_snapshot ? ` · ~${d.estimated_minutes_snapshot}m` : ''}
                {job.price_split === 'days' ? ` · $${Number(d.price_snapshot || 0).toFixed(2)}${d.on_bill ? ` on ${d.on_bill}` : ''}` : ''}
              </Link>
            ))}
          </div>
          {!readOnly && runTotal(job) > 0 && (
            <button onClick={() => setPriceOpen(true)} className="mt-2 text-xs font-semibold text-indigo-800 underline flex items-center gap-1">
              <DollarSign className="h-3.5 w-3.5" /> {job.price_split === 'days' ? 'Change how the price splits across the days' : 'Split the price across the days'}
            </button>
          )}
        </div>
      ) : (!job.continues_job_id && job.status !== 'complete' && job.status !== 'skipped' && !job.recurring_schedule_id) ? (
        <button onClick={() => setSpanOpen(true)} className="text-xs font-medium text-indigo-700 hover:text-indigo-900 flex items-center gap-1">
          <CalendarClock className="h-3.5 w-3.5" /> Takes more than one day? Split it into days
        </button>
      ) : null}

      {spanOpen && <SpanModal job={job} onClose={() => setSpanOpen(false)} onDone={() => { setSpanOpen(false); qc.invalidateQueries(['job', id]); qc.invalidateQueries('jobs-list'); qc.invalidateQueries('calendar-jobs'); }} />}
      {priceOpen && <PriceSplitModal job={job} onClose={() => setPriceOpen(false)} onDone={() => { setPriceOpen(false); qc.invalidateQueries(['job', id]); qc.invalidateQueries('jobs-list'); qc.invalidateQueries('calendar-jobs'); }} />}

      {repeatOpen && (
        <RepeatModal
          job={job}
          onClose={() => setRepeatOpen(false)}
          onDone={() => {
            setRepeatOpen(false);
            qc.invalidateQueries(['job', id]);
            qc.invalidateQueries('routes');
            qc.invalidateQueries('route-stops');
            qc.invalidateQueries('jobs-list');
            qc.invalidateQueries('calendar-jobs');
          }}
        />
      )}

      {rescheduleOpen && (
        <RescheduleModal
          job={job}
          onClose={() => setRescheduleOpen(false)}
          onDone={() => {
            setRescheduleOpen(false);
            qc.invalidateQueries(['job', id]);
            qc.invalidateQueries('routes');
            qc.invalidateQueries('route-stops');
            qc.invalidateQueries('jobs-list');
            qc.invalidateQueries('calendar-jobs');
          }}
        />
      )}

      {timeOpen && (
        <TimeModal
          job={job}
          onClose={() => setTimeOpen(false)}
          onDone={() => {
            setTimeOpen(false);
            qc.invalidateQueries(['job', id]);
            qc.invalidateQueries('routes');
            qc.invalidateQueries('route-stops');
            qc.invalidateQueries('jobs-list');
            qc.invalidateQueries('calendar-jobs');
          }}
        />
      )}

      {/* Details */}
      <div className="card divide-y divide-gray-100">
        <Row icon={Calendar} label="Date">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>{fmtLocalDate(job.scheduled_date, { weekday: 'long', month: 'long', day: 'numeric' })}</span>
            {job.time_start ? (
              <span className={job.time_is_firm ? 'text-red-600 font-bold' : 'text-gray-700'}>
                {job.time_is_firm ? '⏰ ' : ''}{fmtWindow(job)}{job.time_is_firm ? ' · must start on time' : ' · moves as the day runs'}
              </span>
            ) : (
              <span className="text-gray-500">Anytime that day</span>
            )}
            {job.status !== 'complete' && job.status !== 'skipped' && (
              <button onClick={() => setTimeOpen(true)} className="text-xs font-semibold text-brand-600 hover:text-brand-800 flex items-center gap-1">
                <Clock className="h-3 w-3" /> {job.time_start ? 'Change time' : 'Set a time'}
              </button>
            )}
          </span>
        </Row>
        <Row icon={RefreshCw} label="Repeats">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {job.recurring_schedule_id && job.schedule_is_active !== 0 ? (
              <>
                <span className="font-medium">{cadenceLabel(job.schedule_frequency, job.schedule_custom_interval_days, job.schedule_day_of_week)}</span>
                {job.route_name && <span className="text-xs px-2 py-0.5 rounded-full text-snow" style={{ backgroundColor: job.route_color || '#df00ff' }}>{job.route_name}</span>}
                {job.next_visit_date && <span className="text-brand-700 text-xs font-medium">next {fmtLocalDate(job.next_visit_date, { month: 'short', day: 'numeric' })}</span>}
                {job.schedule_end_date && <span className="text-xs text-gray-500">ends {fmtLocalDate(job.schedule_end_date, { month: 'short', day: 'numeric' })}</span>}
              </>
            ) : job.recurring_schedule_id ? (
              <span className="text-gray-500">Series stopped — this visit stays on the books</span>
            ) : (
              <span className="text-gray-500">Does not repeat</span>
            )}
            <button onClick={() => setRepeatOpen(true)} className="text-xs font-semibold text-brand-600 hover:text-brand-800 flex items-center gap-1">
              <Edit2 className="h-3 w-3" /> {job.recurring_schedule_id && job.schedule_is_active !== 0 ? 'Change' : 'Make it repeat'}
            </button>
          </span>
        </Row>
        {job.service_tags && (
          <Row icon={Activity} label="Services">
            <span className="flex flex-wrap gap-1">
              {String(job.service_tags).split(',').filter(Boolean).map((t: string) => (
                <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 capitalize">{t.replace('_', ' ')}</span>
              ))}
            </span>
          </Row>
        )}
        {!!job.is_partial && (
          <Row icon={AlertTriangle} label="Partial">
            <span className="text-amber-700 font-medium">Partially completed — still to do: {job.partial_note}</span>
          </Row>
        )}
        <Row icon={MapPin} label="Address">
          <a href={`https://maps.google.com/?q=${encodeURIComponent(fullAddress)}`} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
            {fullAddress || '—'}
          </a>
        </Row>
        {/* Who is on this job: the people set here, else the crew sent on the route that day.
            A stop still to do on a day offered as one open shift is whoever claims the day: the server refuses
            people ticked onto one job of it (they would be paid for a stop the claimer does), and the Routes page
            refuses its crew — so neither is offered here. Assign stays only to clear people already set on it. */}
        <div className="flex items-start px-4 py-3 gap-3">
          <User className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">People on this job</p>
              {(!offeredDayStop || jobCrewData?.is_override) && (
              <button
                onClick={() => { setCrewPick(new Set((jobCrewData?.crew || []).map((c: any) => c.id))); setCrewEditOpen(v => !v); }}
                className="text-xs text-brand-600 hover:text-brand-700 font-medium flex items-center gap-1">
                <Edit2 className="h-3.5 w-3.5" /> {crewEditOpen ? 'Close' : 'Assign'}
              </button>
              )}
            </div>
            {(jobCrewData?.crew || []).length > 0 ? (
              <div className="flex flex-wrap gap-1.5 mt-1">
                {(jobCrewData?.crew || []).map((c: any) => (
                  <span key={c.id} className="text-xs font-semibold px-2.5 py-1 rounded-full bg-brand-50 text-brand-700 border border-brand-200">
                    {c.name}{c.route_role === 'leader' ? ' · lead' : ''}
                  </span>
                ))}
                <span className="text-[11px] text-gray-500 self-center ml-1">
                  {jobCrewData?.source === 'route' ? (offeredDayStop ? 'claimed the open shift for this day' : 'the crew on this route that day') : jobCrewData?.source === 'job' ? 'set on this job' : jobCrewData?.source_label || ''}
                </span>
              </div>
            ) : offeredDayStop && jobCrewData?.open_shift_stranded ? (
              <p className="text-sm text-gray-500 italic mt-0.5">Nobody is on it and it can't be claimed any more (a stop is already started) — turn the open shift off to pick people.</p>
            ) : offeredDayStop ? (
              <p className="text-sm text-gray-500 italic mt-0.5">Nobody yet — whoever claims the open shift is on it. To pick people yourself, turn the open shift off first.</p>
            ) : (
              <p className="text-sm text-gray-500 italic mt-0.5">Nobody yet — {job.route_id ? 'assign the route for this day in Routes, or pick people here' : 'tap Assign to pick people'}. They see it in their crew app.</p>
            )}
            {crewEditOpen && (
              <div className="mt-3 border-t border-gray-100 pt-3 space-y-2">
                <div className="grid grid-cols-2 gap-1.5">
                  {[...members].sort(byRecentAssignment).map((m: any) => (
                    <label key={m.id} className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border text-sm cursor-pointer ${crewPick.has(m.id) ? 'border-brand-400 bg-brand-50 text-brand-800 font-semibold' : 'border-gray-200 text-gray-700'}`}>
                      <input type="checkbox" checked={crewPick.has(m.id)}
                        onChange={() => setCrewPick(prev => {
                          const next = new Set(prev);
                          if (next.has(m.id)) next.delete(m.id); else next.add(m.id);
                          return next;
                        })}
                        className="rounded border-gray-300 text-brand-600" />
                      <span className="flex-1">{m.name}</span>
                      <span className="text-[10px] text-gray-500 font-normal">{m.last_assigned_date ? `last ${fmtLastWorked(m.last_assigned_date)}` : ''}</span>
                    </label>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => saveJobCrew.mutate({ ids: Array.from(crewPick) })} disabled={readOnly || (saveJobCrew.isLoading)}
                    className="btn-primary text-xs py-1.5 px-3">{saveJobCrew.isLoading ? 'Saving…' : 'Save'}</button>
                  {jobCrewData?.is_override && (
                    <button onClick={() => saveJobCrew.mutate({ ids: [] })} disabled={readOnly || (saveJobCrew.isLoading)}
                      className="text-xs py-1.5 px-3 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100">Clear</button>
                  )}
                  <button onClick={() => setCrewEditOpen(false)} className="btn-secondary text-xs py-1.5 px-3">Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
        {/* Extras that ride this visit — bed spray, a weed treatment, a bush
            trim. The crew sees them, their minutes go into the estimate (which
            is what the crew is paid on), and each becomes its own line on the
            month-end bill. The list is Mowing add-ons. */}
        <AddonsRow job={job} onSaved={() => { qc.invalidateQueries(['job', id]); }} />
        <Row icon={DollarSign} label="Price / Pay">
          <span>
            {job.comped_at
              ? `Free — $${Number(job.comp_value || 0).toFixed(2)} on us${job.comp_reason ? ` (${job.comp_reason})` : ''}`
              : job.price_snapshot ? `$${job.price_snapshot} job` : '—'}
            {Number(job.property_travel_surcharge) > 0 && (job.service_type || 'mowing') === 'mowing'
              ? ` + $${Number(job.property_travel_surcharge).toFixed(0)} travel (${Math.ceil(Number(job.property_travel_miles_out) || 0)} mi outside the service area — its own line at month end)` : ''}
            {effectivePay ? ` · Crew pay $${effectivePay}${job.crew_pay_is_manual ? ' flat, per person' : ' est.'}` : ''}
          </span>
          {job.comped_at && (
            <button onClick={() => { if (window.confirm(`Charge for this visit again ($${Number(job.comp_value || 0).toFixed(2)})?`)) undoFree.mutate(); }}
              disabled={undoFree.isLoading} className="ml-2 text-xs text-gray-500 hover:text-red-700 underline disabled:opacity-50">
              undo free
            </button>
          )}
          {job.no_bill_at && (
            <span className="block text-xs text-amber-900 mt-0.5">
              Not billed — {job.no_bill_reason || 'not owed'} ·{' '}
              <button onClick={() => { if (window.confirm('Bill this visit again? It goes on the next bill.')) undoNoBill.mutate(); }}
                disabled={undoNoBill.isLoading} className="text-gray-600 hover:text-red-700 underline disabled:opacity-50">
                {undoNoBill.isLoading ? 'saving…' : 'undo'}
              </button>
            </span>
          )}
        </Row>
        {job.estimated_minutes_snapshot && !isTodo && (
          <Row icon={Clock} label="Est. Time">{job.estimated_minutes_snapshot} min</Row>
        )}
        {/* A to-do's paid time (owner, Oct 14 2026): hours that pay the people on it, through the same ledger as a
            mow. Who gets it is the crew panel above. */}
        {/* The client a to-do is for (services/todos); its address is the Address row above. */}
        {isTodo && job.todo_customer_name && (
          <Row icon={User} label="For">
            <Link to={`/customers?profile=${job.todo_customer_id}`} className="font-medium text-brand-700 hover:underline">{job.todo_customer_name}</Link>
          </Row>
        )}
        {isTodo && (
          <Row icon={DollarSign} label="Paid time">
            <PaidTimeControl job={job} people={(jobCrewData?.crew || []).map((c: any) => c.name)} readOnly={readOnly}
              onSaved={() => { qc.invalidateQueries(['job', id]); refetchJobCrew(); }} />
          </Row>
        )}
        {/* The plant sizes they picked on the bid (server: services/bushSizes, stamped on the job at acceptance):
            what was agreed, at the price they agreed to. Office only — the crew's card shows the sizes with no money. */}
        {(job.bush_sizes || []).length > 0 && (
          <div className="px-4 py-3 bg-emerald-50">
            <p className="text-xs font-medium text-emerald-800 uppercase tracking-wide mb-1">Plant sizes they picked</p>
            <ul className="space-y-0.5">
              {(job.bush_sizes || []).map((b: any, i: number) => (
                <li key={i} className="text-sm text-emerald-900">
                  {b.quantity > 1 ? `${b.quantity} × ` : ''}{b.description}{b.size_label ? ` — ${b.size_label}` : ''}
                  {b.total != null && <span className="text-emerald-700"> · ${Number(b.total).toFixed(2)}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
        {/* Always shown, so a note can be added without hunting for the property. */}
        <div className={`px-4 py-3 ${job.crew_notes_snapshot ? 'bg-orange-50' : ''}`}>
          <p className="text-xs font-medium text-orange-700 uppercase tracking-wide mb-1">
            Crew Notes <span className="normal-case font-normal text-orange-600">— the crew reads this before starting</span>
          </p>
          {job.crew_notes_snapshot
            ? <p className="text-sm text-orange-900">{job.crew_notes_snapshot}</p>
            : <p className="text-sm text-gray-500">Nothing yet.</p>}
          {(job.crew_note_photos || []).length > 0 && (
            <div className="mt-2"><NotePhotoStrip photos={job.crew_note_photos} canRemove onRemoved={() => qc.invalidateQueries(['job', id])} /></div>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <CrewNoteEditor jobId={Number(id)} notes={job.crew_notes_snapshot} onSaved={() => qc.invalidateQueries(['job', id])} photos={job.crew_note_photos} onPhotosChanged={() => qc.invalidateQueries(['job', id])} />
            <AddNotePhotoButton jobId={Number(id)} scope="this" label="Photo for this visit" onAdded={() => qc.invalidateQueries(['job', id])} className="text-[11px] px-2 py-1 rounded-full border border-gray-300 bg-white text-gray-700 font-medium" />
            <AddNotePhotoButton jobId={Number(id)} scope="future" label="Photo for every visit" onAdded={() => qc.invalidateQueries(['job', id])} className="text-[11px] px-2 py-1 rounded-full border border-gray-300 bg-white text-gray-700 font-medium" />
          </div>
        </div>
        {job.cutting_height_snapshot && (
          <Row icon={Clock} label="Cutting Height">{job.cutting_height_snapshot}</Row>
        )}
        {/* What they bought off a bid that offered several options (server: services/bidOptions.stampJobs) — written
            on the job the moment accept booked the work, so the office knows which version of it this visit is. */}
        {(() => {
          let picked: { title?: string; subtotal?: number }[] = [];
          try { picked = JSON.parse(job.bid_options || '[]') || []; } catch { picked = []; }
          if (!picked.length) return null;
          return (
            <div className="px-4 py-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                Off the bid {picked.length > 1 ? '— they took both' : ''}
              </p>
              <ul className="text-sm text-gray-700 space-y-0.5">
                {picked.map((o, i) => (
                  <li key={i} className="flex justify-between gap-3">
                    <span>{o.title}</span>
                    <span className="tabular-nums text-gray-600">{money(Number(o.subtotal) || 0)}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })()}
        {job.notes && (
          <div className="px-4 py-3">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Internal Notes <span className="normal-case font-normal text-gray-500">— office only, the crew never sees this</span></p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{job.notes}</p>
          </div>
        )}
        {job.gate_notes && <div className="px-4 py-3 bg-blue-50"><p className="text-xs font-semibold text-blue-700 uppercase mb-1">Gate Notes</p><p className="text-sm text-blue-900">{job.gate_notes}</p></div>}
        {job.status === 'skipped' && job.skip_reason && (
          <div className="px-4 py-3 bg-gray-50"><p className="text-xs font-semibold text-gray-500 uppercase mb-1">Skip Reason</p><p className="text-sm text-gray-700">{job.skip_reason}</p></div>
        )}
        {job.actual_start_time && (
          <Row icon={Clock} label="Started At">{fmtTs(job.actual_start_time)}</Row>
        )}
        {job.actual_end_time && (
          <Row icon={Clock} label="Ended At">{fmtTs(job.actual_end_time)}</Row>
        )}
        {job.completed_at && (
          <Row icon={Check} label="Completed At">{fmtTs(job.completed_at)}</Row>
        )}
        {job.actual_minutes != null && (
          <Row icon={Clock} label="Actual Duration">{job.actual_minutes} min</Row>
        )}
        {job.started_by_name && (
          <Row icon={User} label="Started By">{job.started_by_name}
            {job.start_method && job.start_method !== 'manual' && (
              <span className="ml-1 text-xs text-brand-600">
                ({job.start_method === 'geofence_prompt' ? 'geofence prompt' : 'auto-geofence'})
              </span>
            )}
          </Row>
        )}
        {job.completed_by_name && (
          <Row icon={User} label="Completed By">{job.completed_by_name}</Row>
        )}
        {job.late_start_reason && (
          <div className="px-4 py-3 bg-amber-50"><p className="text-xs font-semibold text-amber-700 uppercase mb-1">Late Start Reason</p><p className="text-sm text-amber-900">{job.late_start_reason}</p></div>
        )}
        {job.late_end_reason && (
          <div className="px-4 py-3 bg-amber-50"><p className="text-xs font-semibold text-amber-700 uppercase mb-1">Schedule Variance Reason</p><p className="text-sm text-amber-900">{job.late_end_reason}</p></div>
        )}
        {job.crew_completion_note && (
          <div className="px-4 py-3 bg-brand-50"><p className="text-xs font-semibold text-brand-700 uppercase mb-1">Crew Completion Note</p><p className="text-sm text-brand-900 whitespace-pre-wrap">{job.crew_completion_note}</p></div>
        )}
        {(job.spray_needed || job.spray_notes) && (
          <div className="px-4 py-3 bg-teal-50"><p className="text-xs font-semibold text-teal-700 uppercase mb-1">Spray Garden Beds</p><p className="text-sm text-teal-900">{job.spray_notes || 'Required for this stop'}</p></div>
        )}
      </div>

      {/* Photos */}
      <div className="card">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-base font-black uppercase tracking-tight text-gray-900 flex items-center gap-2"><Camera className="h-4 w-4 text-gray-500" /> Photos</h2>
          <span className="text-sm text-gray-500">{photos.length}</span>
        </div>
        {photos.length === 0 ? (
          <p className="p-6 text-center text-sm text-gray-500">No photos yet.</p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 p-4">
            {photos.map((p: any) => (
              <a key={p.id} href={`${API_BASE}${p.url}`} target="_blank" rel="noopener noreferrer">
                <img src={`${API_BASE}${p.url}`} alt="Job" className="w-full h-28 object-cover rounded-lg border border-gray-200 hover:opacity-90" />
              </a>
            ))}
          </div>
        )}
      </div>

      {/* Line Items */}
      {(lineItems.length > 0 || job.status !== 'complete') && (
        <div className="card">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-base font-black uppercase tracking-tight text-gray-900 flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-gray-500" /> Line Items
            </h2>
            <div className="flex items-center gap-3">
              <button onClick={() => setAiOpen(v => !v)} className="text-xs text-purple-600 hover:text-purple-800 font-medium flex items-center gap-1">
                <Wand2 className="h-3.5 w-3.5" /> AI Quote
              </button>
              {!addingLineItem && (
                <button onClick={() => setAddingLineItem(true)} className="text-xs text-brand-600 hover:text-brand-700 font-medium flex items-center gap-1">
                  <Plus className="h-3.5 w-3.5" /> Add
                </button>
              )}
            </div>
          </div>
          {aiOpen && (
            <div className="px-4 py-3 bg-purple-50/60 border-b border-purple-100">
              <p className="text-[11px] text-purple-700 font-medium mb-1.5">
                Paste your raw notes — AI builds the itemized quote (billable rows added below; either/or and optional work stays in the quote text)
              </p>
              <textarea
                value={aiText}
                onChange={e => setAiText(e.target.value)}
                rows={4}
                placeholder="station 1 replace riser 35, station 5 hunter valve low pressure — diaphragm 95 or whole valve 185…"
                className="input text-sm w-full resize-none"
              />
              <div className="flex gap-2 mt-1.5">
                <button onClick={buildAiQuote} disabled={!aiText.trim() || aiBusy}
                  className="text-xs px-3 py-1.5 rounded-lg bg-purple-600 text-white font-semibold hover:bg-purple-700 disabled:opacity-40 flex items-center gap-1.5">
                  {aiBusy ? <><span className="animate-spin rounded-full h-3 w-3 border-b-2 border-white inline-block" /> Building… (takes ~30s)</> : 'Build itemized quote'}
                </button>
                <button onClick={() => { setAiOpen(false); setAiText(''); setAiQuote(''); }}
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100">Close</button>
              </div>
              {aiQuote && (
                <div className="mt-2.5 border border-purple-300 rounded-lg bg-white">
                  <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-purple-100">
                    <p className="text-[10px] font-bold text-purple-700 uppercase tracking-wide">Quote text — ready to send</p>
                    <button onClick={() => { navigator.clipboard.writeText(aiQuote).then(() => toast.success('Copied')); }}
                      className="text-[11px] px-2 py-1 rounded bg-purple-600 text-white font-semibold hover:bg-purple-700">Copy</button>
                  </div>
                  <pre className="px-2.5 py-2 text-[11px] text-gray-800 whitespace-pre-wrap font-sans max-h-56 overflow-y-auto">{aiQuote}</pre>
                </div>
              )}
            </div>
          )}
          <div className="divide-y divide-gray-100">
            {lineItems.map((li: any) => (
              editingLineItem?.id === li.id ? (
                <div key={li.id} className="px-4 py-3 bg-brand-50 space-y-2">
                  <input value={editingLineItem.description}
                    onChange={e => setEditingLineItem((x: any) => ({ ...x, description: e.target.value }))}
                    className="input text-sm w-full" placeholder="Description" />
                  <div className="flex gap-2">
                    <input type="number" value={editingLineItem.quantity}
                      onChange={e => setEditingLineItem((x: any) => ({ ...x, quantity: e.target.value }))}
                      className="input text-sm w-20" placeholder="Qty" />
                    <input type="number" value={editingLineItem.unit_price}
                      onChange={e => setEditingLineItem((x: any) => ({ ...x, unit_price: e.target.value }))}
                      className="input text-sm flex-1" placeholder="Unit price" step="0.01" />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => updateLineItem.mutate(editingLineItem)} disabled={readOnly || (updateLineItem.isLoading)}
                      className="btn-primary text-xs py-1 px-3">Save</button>
                    <button onClick={() => setEditingLineItem(null)} className="btn-secondary text-xs py-1 px-3">Cancel</button>
                  </div>
                </div>
              ) : (
                <div key={li.id} className="flex items-center justify-between px-4 py-3 group">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800">{li.description}</p>
                    {li.quantity !== 1 && <p className="text-xs text-gray-500">{li.quantity} × ${Number(li.unit_price).toFixed(2)}</p>}
                  </div>
                  <div className="flex items-center gap-2 ml-3">
                    <span className="text-sm font-semibold text-gray-900">${Number(li.total).toFixed(2)}</span>
                    <div className="flex opacity-0 group-hover:opacity-100 transition-opacity gap-1">
                      <button onClick={() => setEditingLineItem({ ...li })} className="p-1 text-gray-500 hover:text-brand-600"><Edit2 className="h-3.5 w-3.5" /></button>
                      <button disabled={readOnly} onClick={() => { if (window.confirm('Remove this item?')) deleteLineItem.mutate(li.id); }}
                        className="p-1 text-gray-500 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                </div>
              )
            ))}
            {lineItems.length === 0 && !addingLineItem && (
              <p className="px-4 py-4 text-sm text-gray-500 italic">No line items. Add items to show them on the invoice.</p>
            )}
            {addingLineItem && (
              <div className="px-4 py-3 bg-brand-50 space-y-2">
                <input value={lineItemForm.description}
                  onChange={e => setLineItemForm(f => ({ ...f, description: e.target.value }))}
                  className="input text-sm w-full" placeholder="Description *" autoFocus />
                <div className="flex gap-2 items-center">
                  <input type="number" value={lineItemForm.quantity}
                    onChange={e => setLineItemForm(f => ({ ...f, quantity: e.target.value }))}
                    className="input text-sm w-20" placeholder="Qty" />
                  <input type="number" value={lineItemForm.unit_price}
                    onChange={e => setLineItemForm(f => ({ ...f, unit_price: e.target.value }))}
                    className="input text-sm flex-1" placeholder="Unit price *" step="0.01" />
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    = ${((Number(lineItemForm.quantity) || 1) * (Number(lineItemForm.unit_price) || 0)).toFixed(2)}
                  </span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => addLineItem.mutate()} disabled={readOnly || (addLineItem.isLoading || !lineItemForm.description || !lineItemForm.unit_price)}
                    className="btn-primary text-xs py-1 px-3">Add</button>
                  <button onClick={() => setAddingLineItem(false)} className="btn-secondary text-xs py-1 px-3">Cancel</button>
                </div>
              </div>
            )}
          </div>
          {lineItems.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
              <span className="text-sm font-bold text-gray-900">Total</span>
              <span className="text-sm font-bold text-green-700">
                ${lineItems.reduce((s: number, li: any) => s + (li.total || 0), 0).toFixed(2)}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Location Events */}
      {locationEvents.length > 0 && (
        <div className="card">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-base font-black uppercase tracking-tight text-gray-900 flex items-center gap-2">
              <Activity className="h-4 w-4 text-gray-500" /> Location Events
            </h2>
            <span className="text-sm text-gray-500">{locationEvents.length}</span>
          </div>
          <div className="divide-y divide-gray-100">
            {locationEvents.map((ev: any) => {
              const distM = ev.distance_meters;
              const distLabel = distM != null
                ? distM < 304.8
                  ? `${Math.round(distM * 3.28084)} ft`
                  : `${(distM / 1609.344).toFixed(1)} mi`
                : null;
              const eventLabels: Record<string, string> = {
                viewed_job: 'Viewed Job', started: 'Started', completed: 'Completed',
                bypass_used: 'Started (Bypass)', arrived: 'Arrived',
              };
              const eventColors: Record<string, string> = {
                started: 'text-blue-600', completed: 'text-green-600',
                bypass_used: 'text-orange-600', viewed_job: 'text-gray-500', arrived: 'text-brand-600',
              };
              const label = eventLabels[ev.event_type] || ev.event_type.replace(/_/g, ' ');
              const color = eventColors[ev.event_type] || 'text-gray-600';
              return (
                <div key={ev.id} className="px-4 py-3 flex items-start gap-3">
                  <Activity className={`h-4 w-4 mt-0.5 flex-shrink-0 ${color}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm font-medium ${color}`}>{label}</span>
                      {ev.within_geofence ? (
                        <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">at site</span>
                      ) : distLabel ? (
                        <span className="text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded">{distLabel} away</span>
                      ) : null}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {fmtTs(ev.created_at)}
                      {ev.employee_name && ` · ${ev.employee_name}`}
                    </p>
                    {ev.bypass_reason && (
                      <p className="text-xs text-orange-700 mt-0.5">Reason: {ev.bypass_reason}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick customer messages (transactional — admin only) */}
      {(job.status === 'scheduled' || job.status === 'in_progress') && (
        <div className="card p-4">
          <h2 className="text-sm font-black uppercase tracking-tight text-gray-900 flex items-center gap-1.5 mb-3">
            <MessageSquare className="h-4 w-4 text-gray-500" /> Customer Messages
          </h2>
          <div className="flex flex-wrap gap-2">
            {[
              { type: 'on_the_way', label: 'On the Way' },
              { type: 'next_stop', label: "You're Next!" },
              { type: 'job_started', label: 'Work Started' },
            ].map(({ type, label }) => (
              <button key={type} onClick={() => openQuick(type)} disabled={!!quickLoading}
                className="btn-secondary text-xs py-1.5 px-3">
                {quickLoading === type ? 'Loading…' : label}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">Sends to customer per their communication preferences.</p>
        </div>
      )}

      {/* Send completion message */}
      {job.status === 'complete' && (
        <div className="card p-4 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-black uppercase tracking-tight text-gray-900">Completion Message</h2>
            {job.completion_message_sent
              ? <p className="text-xs text-green-600 mt-0.5 flex items-center gap-1"><Check className="h-3 w-3" /> Sent {job.completion_message_sent_at ? fmtTs(job.completion_message_sent_at) : ''}</p>
              : <p className="text-xs text-gray-500 mt-0.5">Not sent yet</p>}
          </div>
          <button onClick={() => setMessageOpen(true)} className="btn-primary text-xs py-2">
            <Send className="h-3.5 w-3.5 mr-1" />
            {job.completion_message_sent ? 'Resend' : 'Send Message'}
          </button>
        </div>
      )}

      {/* Quick message modal */}
      {quickMsg && (
        <Modal title={quickMsg.type === 'on_the_way' ? 'On the Way' : quickMsg.type === 'next_stop' ? "You're Next!" : 'Work Started'} onClose={() => setQuickMsg(null)}>
          <div className="space-y-4">
            <div><p className="label-sm">To</p><p className="text-sm text-gray-900">{quickMsg.preview.to_phone || quickMsg.preview.to_email || '(no contact info)'}</p></div>
            <div><p className="label-sm">Message</p>
              <pre className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap font-sans border border-gray-200 max-h-48 overflow-y-auto">{quickMsg.preview.body}</pre>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-800"><strong>Manual send:</strong> Copy the message, text/call the customer, then click "Mark as Sent".</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => { navigator.clipboard.writeText(quickMsg.preview.body); toast.success('Copied!'); }} className="flex-1 btn-secondary">Copy</button>
              <button onClick={() => sendQuick.mutate({ type: quickMsg.type, body: quickMsg.preview.body, recipient: quickMsg.preview.to_phone || quickMsg.preview.to_email || '' })}
                disabled={readOnly || (sendQuick.isLoading)} className="flex-1 btn-primary">
                <Check className="h-4 w-4 mr-1" />{sendQuick.isLoading ? 'Saving…' : 'Mark as Sent'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit modal */}
      {editOpen && editForm && (
        <Modal title="Edit Job" onClose={() => setEditOpen(false)}>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div><label className="label-sm">Date *</label><input type="date" value={editForm.scheduled_date} onChange={e => setEditForm((f: any) => ({ ...f, scheduled_date: e.target.value }))} className="input" /></div>
              <div><label className="label-sm">Start</label><input type="time" value={editForm.time_start} onChange={e => setEditForm((f: any) => ({ ...f, time_start: e.target.value }))} className="input" />
                {(editForm.time_start || editForm.time_end) && <button type="button" onClick={() => setEditForm((f: any) => ({ ...f, time_start: '', time_end: '', time_is_firm: false }))} className="mt-1 text-xs text-gray-600 underline">Clear — anytime that day</button>}</div>
              <div><label className="label-sm">End</label><input type="time" value={editForm.time_end} onChange={e => setEditForm((f: any) => ({ ...f, time_end: e.target.value }))} className="input" /></div>
            </div>
            <label className={`flex items-center gap-2 text-sm rounded-lg px-3 py-2 border cursor-pointer ${editForm.time_is_firm ? 'bg-red-50 border-red-300 text-red-800' : 'border-gray-200 text-gray-600'}`}>
              <input type="checkbox" checked={!!editForm.time_is_firm} onChange={e => setEditForm((f: any) => ({ ...f, time_is_firm: e.target.checked }))} className="rounded border-gray-300 text-red-600" />
              <span><span className="font-semibold">⏰ Must start at this exact time</span> <span className="text-xs opacity-70">— leave off and the crew treats it as an ETA</span></span>
            </label>
            {isTodo && (
              <div><label className="label-sm">Address</label>
                <input type="text" value={editForm.stop_address || ''} onChange={e => setEditForm((f: any) => ({ ...f, stop_address: e.target.value }))}
                  className="input" placeholder="Where it is (optional) — puts it on the Field map" /></div>
            )}
            <p className="text-xs text-gray-500">People are picked on the job page (People on this job), or come from the route assignment for that day.</p>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="label-sm">Price ($)</label><input type="number" value={editForm.price_snapshot} onChange={e => setEditForm((f: any) => ({ ...f, price_snapshot: e.target.value }))} className="input" /></div>
              <div><label className="label-sm">Est. mins</label><input type="number" value={editForm.estimated_minutes_snapshot} onChange={e => setEditForm((f: any) => ({ ...f, estimated_minutes_snapshot: e.target.value }))} className="input" /></div>
              <div><label className="label-sm">Actual mins</label><input type="number" value={editForm.actual_minutes} onChange={e => setEditForm((f: any) => ({ ...f, actual_minutes: e.target.value }))} className="input" placeholder="—" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-sm">Crew pay override ($)</label>
                <input type="number" value={editForm.crew_pay_override} onChange={e => setEditForm((f: any) => ({ ...f, crew_pay_override: e.target.value, crew_pay_is_manual: true }))} className="input" placeholder="Leave blank to auto" />
              </div>
              <div className="flex items-end pb-2">
                {editForm.crew_pay_is_manual && (
                  <button onClick={() => setEditForm((f: any) => ({ ...f, crew_pay_override: '', crew_pay_is_manual: false }))}
                    className="text-xs text-gray-500 hover:text-gray-700">Clear override</button>
                )}
              </div>
            </div>
            <div><label className="label-sm">Status</label>
              <select value={editForm.status} onChange={e => setEditForm((f: any) => ({ ...f, status: e.target.value }))} className="input">
                <option value="scheduled">Scheduled</option><option value="in_progress">In Progress</option>
                <option value="complete">Complete</option><option value="skipped">Skipped</option>
              </select>
            </div>
            <div><label className="label-sm">Notes</label><textarea value={editForm.notes} onChange={e => setEditForm((f: any) => ({ ...f, notes: e.target.value }))} className="input" rows={2} /></div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input type="checkbox" checked={!!editForm.spray_needed} onChange={e => setEditForm((f: any) => ({ ...f, spray_needed: e.target.checked }))} className="rounded text-teal-600" />
                <span className="text-sm font-medium text-teal-700">Spray garden beds</span>
              </label>
              {editForm.spray_needed && (
                <input type="text" value={editForm.spray_notes} onChange={e => setEditForm((f: any) => ({ ...f, spray_notes: e.target.value }))} className="input text-sm" placeholder="Spray instructions (optional)…" />
              )}
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setEditOpen(false)} className="btn-secondary">Cancel</button>
              <button onClick={() => updateJob.mutate({ ...editForm, crew_pay_override: editForm.crew_pay_override ? Number(editForm.crew_pay_override) : null })} disabled={readOnly || (updateJob.isLoading)} className="btn-primary">
                {updateJob.isLoading ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Message modal */}
      {messageOpen && (
        <Modal title="Send Completion Message" onClose={() => setMessageOpen(false)}>
          {!msgPreview ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : (
            <div className="space-y-4">
              <div><p className="label-sm">To</p><p className="text-sm text-gray-900">{msgPreview.to || '(no contact info)'}</p></div>
              <div><p className="label-sm">Subject</p><p className="text-sm text-gray-900">{msgPreview.subject}</p></div>
              <div><p className="label-sm">Message</p>
                <pre className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap font-sans border border-gray-200 max-h-48 overflow-y-auto">{msgPreview.body}</pre>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-800"><strong>Manual send:</strong> Copy the message, send it via text/email, then click "Mark as Sent".</p>
              </div>
              <div className="flex gap-3">
                <button onClick={() => { navigator.clipboard.writeText(msgPreview.body); toast.success('Copied!'); }} className="flex-1 btn-secondary">Copy</button>
                <button onClick={() => markSent.mutate()} disabled={readOnly || (markSent.isLoading)} className="flex-1 btn-primary">
                  <Check className="h-4 w-4 mr-1" />{markSent.isLoading ? 'Recording…' : 'Mark as Sent'}
                </button>
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

// ── Reschedule ────────────────────────────────────────────────────────────────
//
// Two genuinely different operations behind one button.
//
//   Just this one     POST /jobs/:id/move — moves this visit, leaves the
//                     recurring schedule alone. Next time round the customer is
//                     back on their normal day.
//   Every future one  POST /jobs/:id/reschedule-future — moves this visit and
//                     every one after it, and retargets the recurring schedule
//                     so the generator produces the new day from now on.
//
// The second is shown as a preview first. It is a dry run on the server — the
// whole move inside a transaction that gets rolled back — so the numbers are
// what will actually happen, not an estimate of it.
function RescheduleModal({ job, onClose, onDone }: {
  job: any; onClose: () => void; onDone: () => void;
}) {
  const currentDate = String(job.scheduled_date || '').slice(0, 10);
  const isRecurring = !!job.recurring_schedule_id;

  const [toDate, setToDate] = useState(currentDate);
  const [scope, setScope] = useState<'this_only' | 'future'>('this_only');
  const [preview, setPreview] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  const changed = !!toDate && toDate !== currentDate;

  const dayName = (d: string) =>
    d ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }) : '';

  // Any change to the target or the scope invalidates a preview taken before it.
  function setDate(d: string) { setToDate(d); setPreview(null); }
  function setScopeAnd(s: 'this_only' | 'future') { setScope(s); setPreview(null); }

  async function loadPreview() {
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${job.id}/reschedule-future`, {
        to_date: toDate, dry_run: true,
      });
      setPreview(data);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not preview that move');
    } finally { setBusy(false); }
  }

  async function apply() {
    setBusy(true);
    try {
      if (scope === 'this_only') {
        const { data } = await api.post(`/jobs/${job.id}/move`, {
          scheduled_date: toDate, adopt_route: true,
        });
        toast.success(data?.message || 'Moved');
      } else {
        const { data } = await api.post(`/jobs/${job.id}/reschedule-future`, { to_date: toDate });
        toast.success(data?.message || 'Series moved');
      }
      onDone();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not reschedule that job');
    } finally { setBusy(false); }
  }

  const needsPreview = scope === 'future' && !preview;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="text-base font-black uppercase tracking-tight text-gray-900">Reschedule</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500 hover:text-gray-600" /></button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="label-sm">Move to</label>
            <input type="date" value={toDate} onChange={e => setDate(e.target.value)} className="input" />
            <p className="text-xs text-gray-500 mt-1">
              Currently {dayName(currentDate)}
              {changed && <> → <span className="font-medium text-gray-800">{dayName(toDate)}</span></>}
            </p>
          </div>

          <div className="space-y-2">
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="radio" checked={scope === 'this_only'}
                onChange={() => setScopeAnd('this_only')} className="mt-1 text-brand-600" />
              <span>
                <span className="text-sm font-medium text-gray-800">Just this one</span>
                <span className="block text-xs text-gray-500">
                  This visit moves onto the route that runs that day, at its best spot in
                  the order. The recurring schedule is untouched, so the next one is back
                  on the normal day.
                </span>
              </span>
            </label>

            <label className={`flex items-start gap-2 ${isRecurring ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}>
              <input type="radio" checked={scope === 'future'} disabled={!isRecurring}
                onChange={() => setScopeAnd('future')} className="mt-1 text-brand-600" />
              <span>
                <span className="text-sm font-medium text-gray-800">This one and every future visit</span>
                <span className="block text-xs text-gray-500">
                  {isRecurring
                    ? 'Moves the whole series from here on and changes the day it generates on. Past visits stay where they are.'
                    : 'Not available — this is a one-off job, so there is no series behind it.'}
                </span>
              </span>
            </label>
          </div>

          {scope === 'future' && preview && (
            <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2.5 space-y-2">
              <div className="flex gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-900">{preview.message}</p>
              </div>
              {preview.sample?.length > 0 && (
                <div className="text-xs text-amber-800 pl-6">
                  New dates:{' '}
                  {preview.sample.map((s: any) => `${s.d} ${s.dow}${s.status === 'skipped' ? ' (off-week)' : ''}`).join(' · ')}
                </div>
              )}
              {preview.detached > 0 && (
                <p className="text-xs text-amber-800 pl-6">
                  Detached visits become standalone jobs in the Jobs tab. Drag one onto a
                  route on the calendar to put it back on a crew's day.
                </p>
              )}
            </div>
          )}

          {scope === 'future' && !preview && changed && (
            <p className="text-xs text-gray-500">
              Preview first — this changes more than one day.
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-200">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          {needsPreview ? (
            <button onClick={loadPreview} disabled={busy || !changed} className="btn-primary">
              {busy ? 'Checking…' : 'Preview'}
            </button>
          ) : (
            <button onClick={apply} disabled={busy || !changed} className="btn-primary">
              {busy ? 'Moving…' : scope === 'this_only' ? 'Move this visit' : 'Move the series'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const DOW = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
function cadenceLabel(frequency: string | null, customDays: number | null, dow: number | null | undefined) {
  const n = frequency === 'weekly' ? 7 : frequency === 'biweekly' ? 14 : Number(customDays) || 7;
  const every = n === 7 ? 'Every week' : n === 14 ? 'Every 2 weeks' : n % 7 === 0 ? `Every ${n / 7} weeks` : `Every ${n} days`;
  return dow === null || dow === undefined ? every : `${every} on ${DOW[Number(dow)]}`;
}

// ── Multi-day: the whole job's price — day 1's (plus any day with its own), or price_total once split ──────────
function runTotal(job: any): number {
  if (job.price_split === 'days') {
    const sum = (job.run_days || []).reduce((t: number, d: any) => t + (Number(d.price_snapshot) || 0), 0);
    return Math.round((job.price_total != null ? Number(job.price_total) : sum) * 100) / 100;
  }
  const days = job.run_days || [];
  if (days.length > 1) return Math.round(days.reduce((t: number, d: any) => t + (Number(d.price_snapshot) || 0), 0) * 100) / 100;
  return Math.round((Number(job.price_snapshot) || 0) * 100) / 100;
}

// ── Multi-day: how the price splits across the days (POST /jobs/:id/price-split) ───────────────────────────────
// Owner, Oct 22 2026 (Kyle Watson): "I want the price for the job to split as well." Equal by default, an amount
// per day he types (the last day takes what is left), or all on day 1. A day already on a bill keeps its share.
function PriceSplitModal({ job, onClose, onDone }: { job: any; onClose: () => void; onDone: () => void }) {
  const runDays: any[] = job.run_days || [];
  const total = runTotal(job);
  const split = job.price_split === 'days';
  const [mode, setMode] = useState<'equal' | 'amounts' | 'day1'>(split ? 'amounts' : 'equal');
  const [amounts, setAmounts] = useState<Record<string, string>>(() => Object.fromEntries(runDays.map(d => [String(d.id), split && Number(d.price_snapshot) ? String(Number(d.price_snapshot)) : ''])));
  const [busy, setBusy] = useState(false);
  const dead = (d: any) => ['skipped', 'canceled', 'cancelled'].includes(d.status);
  const open = runDays.filter(d => !d.on_bill && !dead(d));
  const billedSum = runDays.filter(d => d.on_bill).reduce((t, d) => t + (Number(d.price_snapshot) || 0), 0);
  const toPlace = Math.round((total - billedSum) * 100) / 100;
  const typedSum = Math.round(open.reduce((t, d) => t + (Number(amounts[String(d.id)]) || 0), 0) * 100) / 100;
  const equalEach = open.length ? Math.round((toPlace / open.length) * 100) / 100 : 0;
  async function save() {
    setBusy(true);
    try {
      const body: any = { mode };
      if (mode === 'amounts') body.amounts = Object.fromEntries(open.map(d => [String(d.id), amounts[String(d.id)] === '' ? null : Number(amounts[String(d.id)])]));
      const { data } = await api.post(`/jobs/${job.id}/price-split`, body);
      toast.success(data?.message || 'Price split');
      onDone();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not split the price');
    } finally { setBusy(false); }
  }
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900">How does the ${total.toFixed(2)} bill?</h3>
            <p className="text-xs text-gray-500 mt-0.5">{runDays.length} days. Each day on its own bill line the month it is finished, or everything once on day 1.</p>
          </div>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500" /></button>
        </div>
        <div className="space-y-1.5">
          {([['equal', `Equal — $${equalEach.toFixed(2)} a day`], ['amounts', 'An amount per day (I type them)'], ['day1', 'Bill it all on day 1']] as [typeof mode, string][]).map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 text-sm text-gray-700">
              <input type="radio" checked={mode === k} onChange={() => setMode(k)} className="text-brand-600" /> {label}
            </label>
          ))}
        </div>
        <div className="space-y-1.5">
          {runDays.map((d, i) => {
            const locked = !!d.on_bill || dead(d);
            const shown = mode === 'amounts' ? amounts[String(d.id)] : mode === 'day1' ? (i === 0 ? String(total) : '0')
              : locked ? String(Number(d.price_snapshot) || 0) : (open[open.length - 1]?.id === d.id ? String(Math.round((toPlace - equalEach * (open.length - 1)) * 100) / 100) : String(equalEach));
            return (
              <div key={d.id} className={`flex items-center gap-2 text-sm ${locked ? 'text-gray-400' : 'text-gray-800'}`}>
                <span className="w-28 shrink-0">Day {i + 1} · {fmtLocalDate(d.scheduled_date, { month: 'short', day: 'numeric' })}</span>
                <span className="inline-flex items-center gap-1">$
                  <input type="number" min={0} step="0.01" value={locked ? String(Number(d.price_snapshot) || 0) : shown ?? ''} disabled={locked || mode !== 'amounts'}
                    onChange={e => setAmounts(a => ({ ...a, [String(d.id)]: e.target.value }))}
                    placeholder={mode === 'amounts' && open[open.length - 1]?.id === d.id ? 'the rest' : ''}
                    className="input w-28 py-1 text-sm disabled:bg-gray-50" />
                </span>
                <span className="text-[11px] text-gray-500 truncate">{d.on_bill ? `on ${d.on_bill} — stays` : dead(d) ? d.status : ''}</span>
              </div>
            );
          })}
        </div>
        {mode === 'amounts' && (
          <p className="text-[11px] text-gray-500">
            {billedSum > 0 ? `$${toPlace.toFixed(2)} to place after the billed days. ` : ''}Typed so far: ${typedSum.toFixed(2)} — leave the last day blank and it takes the rest.
          </p>
        )}
        {mode === 'day1' && billedSum > 0 && <p className="text-[11px] text-red-600">A day is already on a bill, so the price keeps billing day by day.</p>}
        <div className="flex gap-2">
          <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary flex-1 disabled:opacity-50">{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Multi-day: split one job into N linked day jobs ──────────────────────────
function SpanModal({ job, onClose, onDone }: { job: any; onClose: () => void; onDone: () => void }) {
  const current = (job.run_days || []).length || 1;
  const [days, setDays] = useState(Math.max(current + 1, 2));
  const [perDay, setPerDay] = useState<string>('');
  const [skipWeekends, setSkipWeekends] = useState(true);
  const [busy, setBusy] = useState(false);
  const est = Number(job.estimated_minutes_snapshot) || 0;
  // The price, day by day (owner, Oct 22 2026): equal by default, or an amount per day, or all on day 1 as before.
  const price = runTotal(job);
  const [priceMode, setPriceMode] = useState<'equal' | 'amounts' | 'day1'>('equal');
  const [pricePerDay, setPricePerDay] = useState<Record<string, string>>({});
  // Owner, Sep 18 2026: each day has its own crew and its own pay — the split asks.
  const isFlat = !!job.crew_pay_is_manual && job.crew_pay_override != null && job.crew_pay_override !== '';
  const [payMode, setPayMode] = useState<'same_flat' | 'day1_only'>('same_flat');
  const [flatPerDay, setFlatPerDay] = useState<string>(isFlat ? String(job.crew_pay_override) : '');
  const [crewMode, setCrewMode] = useState<'same' | 'per_day'>('same');
  const [crewPerDay, setCrewPerDay] = useState<Record<string, number[]>>({});
  const { data: membersData } = useQuery('crew-members-all', () => api.get('/crews/members/all').then(r => r.data.members as any[]));
  const { data: jobCrewData } = useQuery(['job-crew', String(job.id)], () => api.get(`/jobs/${job.id}/crew`).then(r => r.data));
  const dayOneIds: number[] = ((jobCrewData?.crew || jobCrewData?.members || []) as any[]).map((m: any) => Number(m.crew_member_id ?? m.id)).filter(Boolean);
  const members: any[] = (membersData || []).filter((m: any) => m.is_active !== 0);
  const nameOf = (id: number) => (members.find((m: any) => Number(m.id) === id)?.name || `#${id}`).split(' ')[0];
  const idsFor = (k: number) => crewPerDay[String(k)] ?? dayOneIds;
  const toggle = (k: number, id: number) => setCrewPerDay(p => { const cur = new Set(p[String(k)] ?? dayOneIds); if (cur.has(id)) cur.delete(id); else cur.add(id); return { ...p, [String(k)]: Array.from(cur) }; });
  async function save() {
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${job.id}/span`, {
        days, minutes_per_day: perDay ? Number(perDay) : undefined, skip_weekends: skipWeekends,
        pay_mode: isFlat ? payMode : 'estimate', flat_per_day: isFlat && payMode === 'same_flat' && flatPerDay ? Number(flatPerDay) : undefined,
        crew_per_day: crewMode === 'per_day' ? crewPerDay : undefined,
        ...(price > 0 ? { price_mode: priceMode, price_per_day: priceMode === 'amounts' ? pricePerDay : undefined } : {}),
      });
      if (data?.price_problem) toast.warn(data.message || data.price_problem); else toast.success(data?.message || 'Split into days');
      onDone();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not split the job');
    } finally { setBusy(false); }
  }
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900">How many days?</h3>
            <p className="text-xs text-gray-500 mt-0.5">Starts {fmtLocalDate(job.scheduled_date, { weekday: 'short', month: 'short', day: 'numeric' })}. One linked job per day.</p>
          </div>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500" /></button>
        </div>
        <div className="flex gap-1.5">
          {[2, 3, 4, 5].map(n => (
            <button key={n} type="button" onClick={() => setDays(n)} disabled={n <= current && current > 1}
              className={`press flex-1 py-2 rounded-xl text-sm font-semibold border ${days === n ? 'bg-brand-600 text-black border-brand-600' : 'bg-white text-gray-700 border-gray-200'} disabled:opacity-40`}>{n} days</button>
          ))}
          <input type="number" min={2} max={14} value={days} onChange={e => setDays(Math.min(14, Math.max(2, Number(e.target.value) || 2)))} className="input w-16 text-center py-1.5" />
        </div>
        <div>
          <label className="label-sm">Time per day (minutes)</label>
          <input type="number" min={15} step={15} value={perDay} onChange={e => setPerDay(e.target.value)} className="input"
            placeholder={est ? `${Math.round(est / days)} (splits the ${est} min estimate evenly)` : 'e.g. 480 for a full day'} />
          <p className="text-[11px] text-gray-500 mt-1">Each day pays the crew on its own estimate.</p>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={skipWeekends} onChange={e => setSkipWeekends(e.target.checked)} className="rounded text-brand-600" /> Skip Saturday and Sunday
        </label>

        {/* Who is on each day — pay follows each day's own crew */}
        <div>
          <label className="label-sm">Who's on each day?</label>
          <div className="flex rounded-lg border border-gray-300 overflow-hidden text-xs font-medium mt-1">
            <button type="button" onClick={() => setCrewMode('same')} className={`press flex-1 py-2 ${crewMode === 'same' ? 'bg-brand-600 text-black' : 'bg-white text-gray-700'}`}>Same as day 1{dayOneIds.length ? ` (${dayOneIds.map(nameOf).join(', ')})` : ''}</button>
            <button type="button" onClick={() => setCrewMode('per_day')} className={`press flex-1 py-2 border-l border-gray-300 ${crewMode === 'per_day' ? 'bg-brand-600 text-black' : 'bg-white text-gray-700'}`}>Pick per day</button>
          </div>
          {crewMode === 'per_day' && (
            <div className="mt-2 space-y-2">
              {Array.from({ length: Math.max(0, days - current) }, (_, i) => current + 1 + i).map(k => (
                <div key={k} className="rounded-lg border border-gray-200 p-2">
                  <p className="text-xs font-semibold text-gray-700 mb-1">Day {k}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {members.map((m: any) => (
                      <button key={m.id} type="button" onClick={() => toggle(k, Number(m.id))}
                        className={`press px-2.5 py-1 rounded-full border text-xs ${idsFor(k).includes(Number(m.id)) ? 'bg-brand-600 text-black border-brand-600' : 'bg-white text-gray-700 border-gray-300'}`}>{m.name}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="text-[11px] text-gray-500 mt-1">Each day pays whoever is on it. You can change a day's crew later on that day's job.</p>
        </div>

        {/* The price: equal shares, an amount per day, or all on day 1 (services/priceSplit) */}
        {price > 0 && (
          <div>
            <label className="label-sm">The ${price.toFixed(2)} price</label>
            <div className="space-y-1.5 mt-1">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="radio" checked={priceMode === 'equal'} onChange={() => setPriceMode('equal')} className="text-brand-600" />
                <span>Split it equally — ${(Math.round((price / days) * 100) / 100).toFixed(2)} a day, each day bills when it is done</span>
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="radio" checked={priceMode === 'amounts'} onChange={() => setPriceMode('amounts')} className="text-brand-600" />
                <span>An amount per day</span>
              </label>
              {priceMode === 'amounts' && (
                <div className="grid grid-cols-2 gap-1.5 pl-6">
                  {Array.from({ length: days }, (_, i) => i + 1).map(k => (
                    <label key={k} className="flex items-center gap-1.5 text-xs text-gray-700">Day {k} $
                      <input type="number" min={0} step="0.01" value={pricePerDay[String(k)] ?? ''} placeholder={k === days ? 'the rest' : ''}
                        onChange={e => setPricePerDay(p => ({ ...p, [String(k)]: e.target.value }))} className="input w-24 py-1 text-sm" />
                    </label>
                  ))}
                </div>
              )}
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="radio" checked={priceMode === 'day1'} onChange={() => setPriceMode('day1')} className="text-brand-600" />
                <span>Bill it all on day 1, once the last day is done</span>
              </label>
            </div>
          </div>
        )}

        {/* A flat amount: every day, or day 1 only */}
        {isFlat && (
          <div>
            <label className="label-sm">Flat pay per person</label>
            <div className="space-y-1.5 mt-1">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="radio" checked={payMode === 'same_flat'} onChange={() => setPayMode('same_flat')} className="text-brand-600" />
                <span>Each day pays</span>
                <span className="inline-flex items-center gap-1">$<input type="number" min={0} value={flatPerDay} onChange={e => setFlatPerDay(e.target.value)} className="input w-20 py-1 text-sm" /></span>
                <span>per person</span>
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="radio" checked={payMode === 'day1_only'} onChange={() => setPayMode('day1_only')} className="text-brand-600" />
                <span>Day 1 only — the other days pay by their estimate</span>
              </label>
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary flex-1 disabled:opacity-50">{busy ? 'Splitting…' : `Make it ${days} days`}</button>
        </div>
      </div>
    </div>
  );
}

// ── Time: anytime that day, or a specific arrival time (optionally firm) ──────
function TimeModal({ job, onClose, onDone }: { job: any; onClose: () => void; onDone: () => void }) {
  const isRecurring = !!job.recurring_schedule_id && job.schedule_is_active !== 0;
  const [mode, setMode] = useState<'anytime' | 'time'>(job.time_start ? 'time' : 'anytime');
  const [timeStart, setTimeStart] = useState<string>(String(job.time_start || '09:00').slice(0, 5));
  const [timeEnd, setTimeEnd] = useState<string>(String(job.time_end || '').slice(0, 5));
  const [firm, setFirm] = useState(!!job.time_is_firm);
  const [scope, setScope] = useState<'this' | 'future'>('this');
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      const body = mode === 'anytime'
        ? { time_start: null, time_end: null, time_is_firm: false, scope }
        : { time_start: timeStart, time_end: timeEnd || null, time_is_firm: firm, scope };
      const { data } = await api.post(`/jobs/${job.id}/time`, body);
      toast.success(data?.message || 'Time saved');
      onDone();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not save that time');
    } finally { setBusy(false); }
  }

  const pick = (on: boolean) =>
    `rounded-xl border px-3 py-3 text-sm text-left ${on ? 'border-brand-500 bg-brand-50 text-brand-800' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`;

  return (
    <Modal title="Time for this job" onClose={onClose}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={() => setMode('anytime')} className={pick(mode === 'anytime')}>
            <span className="font-semibold block">Anytime that day</span>
            <span className="text-xs opacity-70">Crew fits it into the day</span>
          </button>
          <button type="button" onClick={() => setMode('time')} className={pick(mode === 'time')}>
            <span className="font-semibold block">Specific time</span>
            <span className="text-xs opacity-70">Shows on the calendar and crew app</span>
          </button>
        </div>

        {mode === 'time' && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-sm">Arrive at</label>
                <input type="time" value={timeStart} onChange={e => setTimeStart(e.target.value)} className={`input ${firm ? 'border-red-400 text-red-700 font-bold' : ''}`} />
              </div>
              <div>
                <label className="label-sm">Until <span className="text-gray-500 font-normal">(optional)</span></label>
                <input type="time" value={timeEnd} onChange={e => setTimeEnd(e.target.value)} className="input" />
              </div>
            </div>
            <label className={`flex items-center gap-2 text-sm rounded-lg px-3 py-2 border cursor-pointer ${firm ? 'bg-red-50 border-red-300 text-red-800' : 'border-gray-200 text-gray-600'}`}>
              <input type="checkbox" checked={firm} onChange={e => setFirm(e.target.checked)} className="rounded border-gray-300 text-red-600" />
              <span><span className="font-semibold">⏰ Must start at this exact time</span> <span className="text-xs opacity-70">— leave off and the crew treats it as an ETA</span></span>
            </label>
            {!!job.route_id && !firm && (
              <p className="text-xs text-gray-500">This stop is on a route, so its ETA moves as earlier stops finish. Tick "must start" to pin the time.</p>
            )}
          </>
        )}

        {isRecurring && (
          <div className="rounded-xl border border-gray-200 divide-y divide-gray-100 text-sm">
            <label className="flex items-center gap-2 px-3 py-2 cursor-pointer">
              <input type="radio" name="time-scope" checked={scope === 'this'} onChange={() => setScope('this')} /> Just this visit
            </label>
            <label className="flex items-center gap-2 px-3 py-2 cursor-pointer">
              <input type="radio" name="time-scope" checked={scope === 'future'} onChange={() => setScope('future')} /> This and all future visits
            </label>
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          <button type="button" onClick={save} disabled={busy || (mode === 'time' && !timeStart)} className="btn-primary flex-1">{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </Modal>
  );
}

// ── Repeats: change the cadence from THIS visit forward, stop it, or make a
//    one-off repeat. The server keeps this visit and everything before it,
//    replaces the planned visits after it, and anchors the new series here.
function RepeatModal({ job, onClose, onDone }: { job: any; onClose: () => void; onDone: () => void }) {
  const jobDate = String(job.scheduled_date || '').slice(0, 10);
  const isRecurring = !!job.recurring_schedule_id && job.schedule_is_active !== 0;
  const currentDays = job.schedule_frequency === 'weekly' ? 7 : job.schedule_frequency === 'biweekly' ? 14 : Number(job.schedule_custom_interval_days) || 14;
  const [days, setDays] = useState<number>(currentDays);
  const [customDays, setCustomDays] = useState<string>(String(currentDays));
  const [dow, setDow] = useState<number>(job.schedule_day_of_week ?? new Date(jobDate + 'T12:00:00').getDay());
  const [endDate, setEndDate] = useState<string>(job.schedule_end_date ? String(job.schedule_end_date).slice(0, 10) : '');
  const [busy, setBusy] = useState(false);
  const presets = [[7, 'Every week'], [14, 'Every 2 weeks'], [21, 'Every 3 weeks'], [28, 'Every 4 weeks'], [0, 'Custom']] as const;
  const effectiveDays = days === 0 ? Math.max(1, parseInt(customDays, 10) || 0) : days;
  const frequency = effectiveDays === 7 ? 'weekly' : effectiveDays === 14 ? 'biweekly' : 'custom';

  async function save() {
    setBusy(true);
    try {
      if (isRecurring) {
        const { data } = await api.put(`/recurring-schedules/${job.recurring_schedule_id}`, {
          frequency, custom_interval_days: frequency === 'custom' ? effectiveDays : null,
          day_of_week: dow, end_date: endDate || null, from_date: jobDate,
        });
        toast.success(data?.message || 'Repeat updated');
      } else {
        const made = await api.post('/recurring-schedules', {
          customer_id: job.customer_id, property_id: job.property_id || null,
          frequency, custom_interval_days: frequency === 'custom' ? effectiveDays : null,
          day_of_week: dow, end_date: endDate || null, time_start: job.time_start || null,
          service_type: job.service_type || 'mowing', route_id: job.route_id || null,
          anchor_date: jobDate, start_date: jobDate, attach_job_id: job.id,
        });
        // When this job's day is not a day its route is out, it stays a one-off and the repeat starts on the route's next run.
        toast.success(made.data?.route_day?.message || 'This job now repeats — upcoming visits are on the calendar');
      }
      onDone();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not update the repeat');
    } finally { setBusy(false); }
  }

  async function stop() {
    if (!window.confirm(`Stop repeating after ${fmtLocalDate(jobDate, { month: 'short', day: 'numeric' })}? Planned visits after it are removed; nothing done is touched.`)) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/recurring-schedules/${job.recurring_schedule_id}`, { params: { remove_after: jobDate } });
      toast.success(data?.message || 'Stopped');
      onDone();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not stop the repeat');
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900">{isRecurring ? 'Change how this repeats' : 'Make this job repeat'}</h3>
            <p className="text-xs text-gray-500 mt-0.5">From {fmtLocalDate(jobDate, { weekday: 'short', month: 'short', day: 'numeric' })} forward. Earlier visits stay as they are.</p>
          </div>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500" /></button>
        </div>

        <div>
          <label className="label-sm">How often</label>
          <div className="grid grid-cols-2 gap-1.5">
            {presets.map(([d, label]) => (
              <button key={label} type="button" onClick={() => setDays(d)}
                className={`press px-3 py-2 rounded-xl text-sm font-medium border ${days === d ? 'bg-brand-600 text-black border-brand-600' : 'bg-white text-gray-700 border-gray-200 hover:border-brand-300'}`}>
                {label}
              </button>
            ))}
          </div>
          {days === 0 && (
            <div className="flex items-center gap-2 mt-2 text-sm text-gray-700">
              Every <input type="number" min={1} max={365} value={customDays} onChange={e => setCustomDays(e.target.value)} className="input w-20 text-center py-1" /> days
            </div>
          )}
        </div>

        <div>
          <label className="label-sm">On which day</label>
          <div className="flex gap-1 flex-wrap">
            {DOW.map((name, i) => (
              <button key={name} type="button" onClick={() => setDow(i)}
                className={`w-10 h-9 rounded-full text-xs font-semibold border ${dow === i ? 'bg-brand-600 text-black border-brand-600' : 'bg-white text-gray-600 border-gray-200 hover:border-brand-300'}`}>
                {name.slice(0, 2)}
              </button>
            ))}
          </div>
          {dow !== new Date(jobDate + 'T12:00:00').getDay() && (
            <p className="text-[11px] text-amber-700 mt-1">This visit stays on {fmtLocalDate(jobDate, { weekday: 'long' })}; the next ones land on {DOW[dow]}s.</p>
          )}
        </div>

        <div>
          <label className="label-sm">Ends</label>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setEndDate('')} className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${!endDate ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-600 border-gray-200'}`}>Never</button>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="input w-auto py-1.5 text-sm" />
          </div>
        </div>

        <p className="text-xs text-gray-500">{cadenceLabel(frequency, frequency === 'custom' ? effectiveDays : null, dow)}{endDate ? ` until ${fmtLocalDate(endDate, { month: 'short', day: 'numeric' })}` : ''}{job.route_name ? ` · stays on ${job.route_name}` : ''}</p>

        <div className="flex gap-2">
          {isRecurring && (
            <button onClick={stop} disabled={busy} className="px-3 py-2 rounded-xl border border-red-200 text-red-700 text-sm font-semibold hover:bg-red-50 disabled:opacity-50">Stop after this visit</button>
          )}
          <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          <button onClick={save} disabled={busy || effectiveDays < 1} className="btn-primary flex-1 disabled:opacity-50">{busy ? 'Saving…' : isRecurring ? 'Apply from here' : 'Start repeating'}</button>
        </div>
      </div>
    </div>
  );
}

/**
 * The extras on this visit.
 *
 * The owner, Sep 17 2026: "for bed spray, make that a mowing add-on … a new
 * tab that just says mowing add-ons … and we can add it on to a mowing job."
 * The list lives on Mowing add-ons; a treatment is priced off the cost sheet
 * for this yard, everything else at the price set there. Either way the price
 * can be changed for this one visit.
 */
/** The schedule's standing discount: $ off every mow, with the words her bill shows. */
function StandingDiscount({ scheduleId, readOnly, onSaved }: { scheduleId: number; readOnly: boolean; onSaved: () => void }) {
  const qc = useQueryClient();
  const { data } = useQuery(['schedule-discount', scheduleId], () => api.get(`/recurring-schedules/${scheduleId}/discount`).then(r => r.data));
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [label, setLabel] = useState('');
  const save = useMutation((body: { amount: string | number; label?: string }) => api.put(`/recurring-schedules/${scheduleId}/discount`, body).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d.message); setOpen(false); qc.invalidateQueries(['schedule-discount', scheduleId]); onSaved(); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not save the discount'); },
  });
  const on = Number(data?.amount) > 0;
  if (!data) return null;
  return (
    <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-900">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-amber-800">Discount on every mow</span>
        {on
          ? <span><span className="font-semibold">${Number(data.amount).toFixed(2)} off</span> — {data.label}</span>
          : <span className="text-gray-500">none</span>}
        {!open && (
          <span className="ml-auto flex items-center gap-3">
            <button onClick={() => { setAmount(on ? String(data.amount) : ''); setLabel(on ? String(data.label || '') : ''); setOpen(true); }}
              disabled={readOnly} className="text-xs text-brand-600 hover:text-brand-700 font-medium disabled:opacity-40">{on ? 'Change' : 'Give one'}</button>
            {on && (
              <button onClick={() => { if (window.confirm('Stop this discount? Mows not finished yet will not get it. Mows already finished keep theirs.')) save.mutate({ amount: 0 }); }}
                disabled={readOnly || save.isLoading} className="text-xs text-gray-500 hover:text-red-600 disabled:opacity-40">stop</button>
            )}
          </span>
        )}
      </div>
      {open && (
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <label className="text-xs text-gray-600">$ off each mow
            <input value={amount} onChange={e => setAmount(e.target.value)} inputMode="decimal" placeholder="5" className="input mt-1 w-24" />
          </label>
          <label className="text-xs text-gray-600 flex-1 min-w-[12rem]">What their bill says
            <input value={label} onChange={e => setLabel(e.target.value)} placeholder="Special referral discount" maxLength={80} className="input mt-1 w-full" />
          </label>
          <button onClick={() => save.mutate({ amount, label })} disabled={save.isLoading || amount.trim() === ''} className="btn-primary text-xs py-1.5 px-3 disabled:opacity-40">{save.isLoading ? 'Saving…' : 'Save'}</button>
          <button onClick={() => setOpen(false)} className="btn-secondary text-xs py-1.5 px-3">Cancel</button>
        </div>
      )}
      <p className="text-[11px] text-amber-900/70 mt-1">Every mow of this schedule gets it when the mow is finished, from the day you set it. Their bill shows the price, then the discount.</p>
    </div>
  );
}

function AddonsRow({ job, onSaved }: { job: any; onSaved: () => void }) {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState('');
  const [price, setPrice] = useState('');
  const [minutes, setMinutes] = useState('');
  // '' = just this visit; otherwise a cadence that rides the mowing schedule from this visit on.
  const [often, setOften] = useState('');
  // How many times, counted from this visit ('' = until it is stopped). "3 weed treatments for $150" is three, not
  // every mow forever (audit, Oct 1 2026).
  const [times, setTimes] = useState('');
  const scheduleId: number | null = job?.recurring_schedule_id || null;

  const type = String(job?.service_type || '').toLowerCase();
  const routeWork = !['estimate', 'todo', 'fix'].includes(type);

  const { data: onJob } = useQuery(['job-addons', job.id], () => api.get(`/mow-addons/job/${job.id}`).then(r => r.data.addons as any[]), { enabled: routeWork });
  // Priced for this visit: riding a mow there is no drive in a treatment's price; a visit of its own carries it.
  const { data: menu } = useQuery(['addon-menu', job.property_id, job.id], () => api.get('/mow-addons', { params: { property_id: job.property_id, job_id: job.id } }).then(r => r.data.addons as any[]), { enabled: routeWork && open });

  // What rides the whole schedule (owner, Sep 19 2026: "once a month or every mow or every other mow or whatever").
  const { data: standing } = useQuery(['schedule-addons', scheduleId], () => api.get(`/mow-addons/schedule/${scheduleId}`).then(r => r.data), { enabled: routeWork && !!scheduleId });
  const rides: any[] = standing?.addons || [];
  // A rule on the list's price goes back with no price: sent back, today's list price became the rule's own and
  // re-priced a visit already done (audit, Oct 1 2026).
  const putRule = (list: any[]) => api.put(`/mow-addons/schedule/${scheduleId}`, { addons: ruleBody(list) }).then(r => r.data);
  const afterRule = (d: any) => { toast.success(d.message); qc.invalidateQueries(['schedule-addons', scheduleId]); qc.invalidateQueries(['job-addons', job.id]); onSaved(); };
  const add = useMutation(() => (often && scheduleId
    ? putRule([...rides.filter(a => a.key !== pick), { key: pick, cadence: often, price: price === '' ? undefined : price, minutes: minutes === '' ? undefined : minutes, from: String(job.scheduled_date || '').slice(0, 10), times: Number(times) > 0 ? Math.round(Number(times)) : undefined }])
    : api.post(`/mow-addons/job/${job.id}`, { key: pick, price: price === '' ? undefined : price, minutes: minutes === '' ? undefined : minutes }).then(r => r.data)), {
    onSuccess: (d: any) => { setOpen(false); setPick(''); setPrice(''); setMinutes(''); setOften(''); setTimes(''); if (often) afterRule(d); else { toast.success(d.message); qc.invalidateQueries(['job-addons', job.id]); onSaved(); } },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not add that'); },
  });
  const changeRule = useMutation((list: any[]) => putRule(list), { onSuccess: afterRule, onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not change that'); } });
  const drop = useMutation((addonId: number) => api.delete(`/mow-addons/job-addon/${addonId}`).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d.message); qc.invalidateQueries(['job-addons', job.id]); onSaved(); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not take that off'); },
  });

  if (!routeWork) return null;
  const rows: any[] = onJob || [];
  const chosen = (menu || []).find((m: any) => m.key === pick);

  return (
    <div className="flex items-start px-4 py-3 gap-3">
      <Leaf className={`h-4 w-4 mt-0.5 flex-shrink-0 ${rows.length ? 'text-emerald-600' : 'text-gray-500'}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Add-ons on this visit</p>
          <span className="flex items-center gap-3">
            <Link to="/lawn-care?tab=addons" className="text-xs text-gray-500 hover:text-gray-600">Edit the list</Link>
            <button onClick={() => setOpen(v => !v)} className="text-xs text-brand-600 hover:text-brand-700 font-medium">{open ? 'Close' : 'Add one'}</button>
          </span>
        </div>

        {rows.length === 0 ? (
          <p className="text-sm text-gray-500 italic mt-0.5">Nothing extra on this visit.</p>
        ) : (
          <ul className="mt-1 space-y-1">
            {rows.map(a => (
              <li key={a.id} className="flex items-center gap-2 text-sm text-gray-900">
                <span className="font-medium">{a.name}</span>
                <span className="text-gray-500">${Number(a.price || 0).toFixed(2)}{a.minutes ? ` · ${a.minutes} min` : ''}{a.invoiced_at ? ' · billed' : ''}</span>
                {!a.invoiced_at && (
                  <button onClick={() => drop.mutate(a.id)} disabled={readOnly || (drop.isLoading)} className="text-gray-300 hover:text-red-600" title="Take it off">
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* What rides every visit of this mowing schedule, and how often (components/StandingAddonRules, shared with Lawn care → Add-ons) */}
        <StandingAddonRules rides={rides} cadences={standing?.cadences || []} from={String(job.scheduled_date || '').slice(0, 10)}
          busy={changeRule.isLoading} readOnly={readOnly} onChange={list => changeRule.mutate(list)} />

        {/* Money off every mow of this schedule, for good (owner, Oct 7 2026: "a permanent $5 discount") */}
        {scheduleId && <StandingDiscount scheduleId={scheduleId} readOnly={readOnly} onSaved={() => { qc.invalidateQueries(['job-addons', job.id]); onSaved(); }} />}

        {open && (
          <div className="mt-2 flex flex-wrap items-end gap-2">
            <label className="text-xs text-gray-600">Add
              <select value={pick} onChange={e => { setPick(e.target.value); const m = (menu || []).find((x: any) => x.key === e.target.value); setPrice(m ? String(m.price) : ''); setMinutes(m ? String(m.minutes) : ''); }}
                className="input mt-1 w-56">
                <option value="">Pick one…</option>
                {(menu || []).map((m: any) => <option key={m.key} value={m.key}>{m.name} — ${Number(m.price).toFixed(0)}{m.sheet_priced ? ' (sheet)' : ''}</option>)}
              </select>
            </label>
            <label className="text-xs text-gray-600">Price
              <input value={price} onChange={e => setPrice(e.target.value)} inputMode="decimal" className="input mt-1 w-24" />
            </label>
            <label className="text-xs text-gray-600">Minutes
              <input value={minutes} onChange={e => setMinutes(e.target.value)} inputMode="numeric" className="input mt-1 w-20" />
            </label>
            {scheduleId && (
              <label className="text-xs text-gray-600">How often
                <select value={often} onChange={e => setOften(e.target.value)} className="input mt-1 w-44">
                  <option value="">Just this visit</option>
                  {(standing?.cadences || []).map((c: any) => <option key={c.key} value={c.key}>{c.label}, from this visit</option>)}
                </select>
              </label>
            )}
            {scheduleId && often && (
              <label className="text-xs text-gray-600">How many times
                <input value={times} onChange={e => setTimes(e.target.value.replace(/[^0-9]/g, ''))} inputMode="numeric" placeholder="until stopped" className="input mt-1 w-28" />
              </label>
            )}
            <button onClick={() => add.mutate()} disabled={readOnly || (!pick || add.isLoading)} className="btn-primary text-xs py-2 px-3 disabled:opacity-50">
              {add.isLoading ? 'Adding…' : often ? 'Put it on the schedule' : 'Add to this visit'}
            </button>
            {chosen?.priced_from && <p className="w-full text-[11px] text-gray-500">{chosen.name}: {chosen.priced_from}.</p>}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Open shift ────────────────────────────────────────────────────────────────
// Who the shift is offered to, who claimed it, and texting them. All of it can
// be changed while the job is still scheduled — after it's offered and after it
// fills (owner, Sep 30 2026: "let me adjust open shift aswell when its done").
// Date and time still change through the job's normal Edit.
//
// Several jobs in one day can be ONE open shift (owner, Oct 8 2026: "implement
// ith multiple dif oibs in one day to get the guys to move to the next job
// without having to request another open shift thing"): they sit on a one-day
// route, one claim takes them all, and the claimer is on that route for the
// day. The panel is then the same from any of those jobs and every button
// acts on all of them — the server sends `bundle` (which jobs go together) and
// counts for the whole day, and the words here say "all 3 jobs" where they
// used to say "this shift".
const OPEN_SHIFT_MOWING_TYPES = new Set(['mowing', 'fertilizer', 'aeration', 'seeding']);
const OFF_STATUSES = ['skipped', 'canceled', 'cancelled'];

interface OpenShiftInfo {
  job_id: number; status: string; is_open_shift: boolean; filled: boolean; picked: boolean;
  required: number; claimed_count: number;
  offered: { id: number; name: string; has_phone: boolean; texts_on: boolean; notified_at: string | null; claimed_at: string | null }[];
  claims: { id: number; name: string; claimed_at: string }[];
  // The day this job is offered with, its jobs in the order they are driven. null for a shift of its own.
  bundle?: {
    route_id: number; route_name: string; date: string; started: boolean;
    jobs: { id: number; customer_name: string | null; title: string | null; status: string }[];
  } | null;
}

const nameList = (names: string[]) => names.length <= 1 ? (names[0] || '')
  : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

function OpenShiftPanel({ job, jobId, readOnly }: { job: any; jobId: string; readOnly: boolean }) {
  const qc = useQueryClient();
  const claimsOnJob = Number(job.open_shift_claimed_count) || 0;
  // One of the jobs of a day offered as one open shift. A stop that was skipped or cancelled has left it.
  const onOfferedDay = Number(job.route_open_shift) === 1 && !OFF_STATUSES.includes(String(job.status || ''));
  // A filled shift keeps its panel: the owner can still take someone off or reopen it.
  // So does a picked shift he turned off, so Reopen goes back to the same people.
  const hasShift = !!job.is_open_shift || claimsOnJob > 0 || Number(job.open_shift_picked) === 1 || onOfferedDay;
  const scheduled = job.status === 'scheduled';
  const isMowingJob = OPEN_SHIFT_MOWING_TYPES.has(String(job.service_type || 'mowing').toLowerCase());
  // A route stop is covered by whoever runs the route, so there's nothing to claim.
  const canOffer = !hasShift && scheduled && !isMowingJob && !job.route_id;

  const { data: info } = useQuery<OpenShiftInfo>(['open-shift', jobId],
    () => api.get(`/jobs/${jobId}/open-shift`).then(r => r.data),
    { enabled: hasShift });

  // The day it is offered with: the jobs still on it (a skipped stop is not one of them), and how sentences name them.
  const day = info?.bundle || null;
  const dayJobs = day ? day.jobs.filter(o => !OFF_STATUSES.includes(String(o.status || ''))) : [];
  const dayWhen = day ? shortDay(day.date) : '';
  const allOfDay = allJobsWord(dayJobs.length);   // "all 3 jobs"
  // Once a stop of the day is started nobody new can claim it — everyone on it does every job.
  const dayStarted = !!day?.started;

  // A loose job nobody has claimed, on a day with other loose jobs: the Calendar's "Route loose jobs" sheet can
  // put them out together as one open shift. Offered from here only when this job and at least one other may go in.
  const dayISO = String(job.scheduled_date || '').slice(0, 10);
  const couldJoinDay = scheduled && !isMowingJob && !job.route_id && claimsOnJob === 0
    && /^\d{4}-\d{2}-\d{2}$/.test(dayISO) && dayISO >= businessToday();
  const { data: dayOffer } = useQuery(offerJobsKey(dayISO), () => fetchOfferJobs(dayISO), { enabled: couldJoinDay, staleTime: 60_000 });
  const offerDayLink = couldJoinDay && !!dayOffer
    && dayOffer.some(o => Number(o.job_id) === Number(jobId) && !o.cant_offer)
    && dayOffer.some(o => Number(o.job_id) !== Number(jobId) && !o.cant_offer) ? (
      <Link to={`/calendar?route_day=${dayISO}&offer=1`} className="text-xs font-medium text-orange-700 hover:text-orange-900 underline">
        Offer the day's jobs as one open shift
      </Link>
    ) : null;

  const [editing, setEditing] = useState(false);
  const [to, setTo] = useState<'everyone' | 'picked'>('everyone');
  const [picks, setPicks] = useState<number[]>([]);
  const [needed, setNeeded] = useState(1);
  // Texting not set up: the messages to send from his phone — from the buttons
  // here, or left by Create Job's "Text them now".
  const [shiftDrafts, setShiftDrafts] = useState<ShiftDraft[]>(() =>
    (qc.getQueryData(['open-shift-drafts', jobId]) as ShiftDraft[] | undefined) || []);
  const [skipped, setSkipped] = useState<{ id: number; name: string; why: string }[]>([]);

  const open = info ? !!info.is_open_shift : !!job.is_open_shift;
  const claimed = info ? Number(info.claimed_count) || 0 : claimsOnJob;
  const required = info ? Number(info.required) || 1 : (Number(job.open_shift_required_count) || 1);
  const filled = info ? !!info.filled : (claimed > 0 && claimed >= required);
  const mode: 'everyone' | 'picked' = info?.picked ? 'picked' : 'everyone';
  const offeredIds = (info?.offered || []).map(o => o.id);

  function refresh(r?: any) {
    if (r?.job_id) qc.setQueryData(['open-shift', jobId], r);
    qc.invalidateQueries(['job', jobId]);
    qc.invalidateQueries(['open-shift', jobId]);
    qc.invalidateQueries(['job-crew', jobId]);
    qc.invalidateQueries('jobs-list');
    // Offered with other jobs: the same change landed on every one of them, so their pages read themselves again too.
    for (const o of day?.jobs || []) {
      if (String(o.id) === String(jobId)) continue;
      qc.invalidateQueries(['job', String(o.id)]);
      qc.invalidateQueries(['open-shift', String(o.id)]);
      qc.invalidateQueries(['job-crew', String(o.id)]);
    }
  }

  // Default: only the people not texted yet. "Text everyone again" is the deliberate re-send.
  const notifyShift = useMutation((resend: boolean) =>
    api.post(`/jobs/${jobId}/notify-shift`, resend ? { resend: true } : {}).then(r => r.data), {
    onSuccess: (r: any) => {
      if (r?.pending) return;
      setShiftDrafts(r?.drafts || []);
      setSkipped(r?.skipped || []);
      ((r?.notified || r?.sent) ? toast.success : toast.info)(r?.message || 'Done');
      refresh();
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not text the crew'); },
  });

  const saveShift = useMutation((body: any) => api.put(`/jobs/${jobId}/open-shift`, body).then(r => r.data), {
    onSuccess: (r: any, body: any) => {
      setEditing(false);
      if (r?.pending) return;
      refresh(r);
      const removed: string[] = r?.removed_already_texted || [];
      const kept: string[] = r?.kept_claimers || [];
      // Turning off a day offered as one open shift comes back with what is left: the route, and who is still on it.
      toast.success(body.on === false ? (r?.message || 'Open shift turned off — claims stay')
        : r?.is_open_shift ? `Open for the crew to claim — ${r.claimed_count} of ${r.required} claimed`
        : r?.filled ? `Filled — ${r.claimed_count} of ${r.required} claimed`
        : 'Saved');
      // Fewer People needed slowed the day and a set time is now reached late: the server says which and what to do.
      if (body.on !== false && r?.message) toast.warn(r.message, { autoClose: 12000 });
      if (removed.length) toast.warn(`${nameList(removed)} already got a text about it — let them know it's off for them.`, { autoClose: 10000 });
      if (kept.length) toast.info(`${nameList(kept)} already claimed, so they stay on it. Use "take off" to free a spot.`, { autoClose: 8000 });
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not save the open shift'); },
  });

  // Take a claimer off: their spot opens again. Nobody is texted.
  const releaseClaim = useMutation((cm: { id: number; name: string }) =>
    api.delete(`/jobs/${jobId}/open-shift/claims/${cm.id}`).then(r => r.data), {
    onSuccess: (r: any, cm) => {
      if (r?.pending) return;
      refresh(r);
      // Off a whole day: say so — they are off every job of it, not only the one on this page.
      toast.success(`${r?.released || cm.name} is off ${day ? `${allOfDay} on ${dayWhen}` : 'this shift'}.${r?.reopened ? ' It\'s open again — the crew can claim the spot.' : ''}`);
      // The last person off a day already started: nobody can claim it now, so the server says what to do next.
      if (r?.message) toast.warn(r.message, { autoClose: 12000 });
      // A tip on a stop already finished that day, part of which had gone to them (services/tipShare).
      for (const w of r?.tip_warnings || []) toast.warn(w, { autoClose: false });
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not take them off'); },
  });

  function startEdit() {
    setTo(mode);
    // Switching from anyone to picked starts from the people who claimed — they stay either way.
    setPicks(mode === 'picked' ? offeredIds : (info?.claims || []).map(c => c.id));
    setNeeded(required);
    setEditing(true);
  }
  function startOffer() {
    setTo('everyone'); setPicks([]); setNeeded(Number(job.open_shift_required_count) || 1);
    setEditing(true);
  }
  // Keeps who it's offered to exactly as it is (turn off, reopen).
  const sameOffer = () => ({ to: mode, ...(mode === 'picked' ? { crew_member_ids: offeredIds } : {}) });
  const busy = readOnly || saveShift.isLoading || releaseClaim.isLoading;

  const editor = (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <label htmlFor={`open_shift_needed_${jobId}`} className="text-sm text-gray-600">People needed:</label>
        <PeopleNeededInput id={`open_shift_needed_${jobId}`} value={needed} onChange={setNeeded} />
        {claimed > 0 && needed <= claimed ? (
          <span className="text-xs text-amber-700">{claimed} already claimed — that fills it</span>
        ) : filled && needed > claimed ? (
          <span className="text-xs text-amber-700">Opens it again for {needed - claimed} more</span>
        ) : null}
      </div>
      {day && <p className="text-[11px] text-gray-500">People needed is for the whole day — everyone who claims does {allOfDay}.</p>}
      <div>
        <p className="label-sm">Who can claim it</p>
        <div className="space-y-1">
          {([['everyone', 'Anyone on the crew app'], ['picked', 'Only people I pick']] as const).map(([val, lbl]) => (
            <label key={val} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="radio" name={`open_shift_to_${jobId}`} checked={to === val} onChange={() => setTo(val)}
                className="text-brand-600 focus:ring-brand-500" />
              {lbl}
            </label>
          ))}
        </div>
      </div>
      {to === 'picked' && (
        <div className="bg-white border border-orange-200 rounded-lg p-2.5">
          <PeoplePicker value={picks} onChange={setPicks} />
          {picks.length === 0 ? (
            <p className="text-xs text-amber-700 mt-1.5">Pick at least one person.</p>
          ) : picks.length < needed ? (
            <p className="text-xs text-amber-700 mt-1.5">{picks.length} picked for {needed} needed — it can't fill unless you add more.</p>
          ) : null}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => saveShift.mutate({ on: true, to, required_count: needed, ...(to === 'picked' ? { crew_member_ids: picks } : {}) })}
          disabled={busy || (to === 'picked' && picks.length === 0)}
          className="btn-primary text-xs py-1.5 px-3 disabled:opacity-40">
          {saveShift.isLoading ? 'Saving…' : !hasShift ? 'Offer it'
            // Turned off and not full: saving puts it back on the crew's Open tab — say so.
            : !open && !filled && !(claimed > 0 && needed <= claimed) ? 'Save and reopen' : 'Save'}
        </button>
        <button onClick={() => setEditing(false)} className="btn-secondary text-xs py-1.5 px-3">Cancel</button>
        <span className="text-[11px] text-gray-500">
          {hasShift ? 'Anyone new isn\'t texted until you tap "Text the ones not texted yet".' : 'Nobody is texted yet — the Text button shows once it\'s offered.'}
        </span>
      </div>
    </div>
  );

  if (!hasShift) {
    if (!canOffer) return null;
    return (
      <div className={`rounded-xl border px-4 py-3 ${editing ? 'border-orange-200 bg-orange-50' : 'border-gray-200 bg-white'}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-900">Open shift</p>
            {!editing && <p className="text-xs text-gray-500">Not offered — offer it and the crew can claim it.</p>}
          </div>
          {!editing && (
            <button onClick={startOffer} disabled={readOnly} className="text-xs font-medium text-orange-700 hover:text-orange-900 disabled:opacity-50">
              Offer as open shift
            </button>
          )}
        </div>
        {/* Other loose jobs that day: they can all go out as one open shift, claimed once (Calendar → Route loose jobs). */}
        {!editing && offerDayLink && <p className="mt-1.5">{offerDayLink}</p>}
        {editing && <div className="mt-2">{editor}</div>}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-orange-200 bg-orange-50 px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold bg-orange-100 text-orange-800 border border-orange-200 px-2 py-0.5 rounded-full">
          {open ? 'OPEN SHIFT' : filled ? 'Open shift — filled' : dayStarted ? 'Open shift — already started' : 'Open shift — off'}
        </span>
        <span className="text-sm text-orange-900">{claimed} of {required} claimed</span>
        {open && (
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {/* One link for everybody — the crew's Open shifts tab, the one the text carries. */}
            <button onClick={copyOpenShiftLink} className="text-xs text-orange-800 hover:text-orange-950 underline">
              Copy link
            </button>
            <button
              onClick={() => { if (window.confirm(`Text everyone it's offered to again${mode === 'everyone' ? ' (everyone on the crew app)' : ''}? People who already got a text get another one.${day ? ` It is one text for ${allOfDay}.` : ''}`)) notifyShift.mutate(true); }}
              disabled={readOnly || notifyShift.isLoading}
              className="text-xs text-orange-800 hover:text-orange-950 underline disabled:opacity-50">
              Text everyone again
            </button>
            <button
              onClick={() => notifyShift.mutate(false)}
              disabled={readOnly || notifyShift.isLoading}
              className="text-xs px-3 py-1.5 rounded-lg bg-orange-600 text-white font-semibold inline-flex items-center gap-1 disabled:opacity-50">
              <MessageSquare className="h-3.5 w-3.5" /> {notifyShift.isLoading ? 'Texting…' : 'Text the ones not texted yet'}
            </button>
          </div>
        )}
      </div>

      {/* Offered with other jobs that day: which ones, in the order they are driven — each a link to its own page,
          where this same panel shows. One claim takes them all, and so does every button here. */}
      {day && (
        <div className="mt-2 text-xs text-orange-900">
          <p className="font-semibold">Part of one open shift — {jobsWord(dayJobs.length)} on {dayWhen}</p>
          <ol className="mt-1 space-y-0.5">
            {dayJobs.map((o, i) => {
              const label = `${o.customer_name || o.title || `Job ${o.id}`}${o.customer_name && o.title ? ` — ${o.title}` : ''}`;
              const state = o.status === 'complete' ? ' · done' : o.status === 'in_progress' ? ' · started' : '';
              return (
                <li key={o.id}>
                  {i + 1}.{' '}
                  {String(o.id) === String(jobId)
                    ? <span className="font-semibold">{label} <span className="font-normal text-orange-800">(this job)</span></span>
                    : <Link to={`/jobs/${o.id}`} className="underline hover:text-orange-950">{label}</Link>}
                  {state}
                </li>
              );
            })}
          </ol>
          <p className="mt-1 text-orange-800">
            {dayStarted
              ? 'A stop is already started, so nobody else can claim it.'
              : `${open ? `One claim takes ${allOfDay}. ` : ''}Anything you change here changes ${allOfDay}.`}
          </p>
        </div>
      )}

      {/* Who it's offered to — texted and/or claimed */}
      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
        <span className="font-semibold text-orange-900">Offered to:</span>
        {!info ? <span className="text-gray-500">…</span>
          : !info.picked ? <span className="text-orange-900">anyone on the crew app{info.offered.length ? ' · texted so far:' : ''}</span>
          : info.offered.length === 0 ? <span className="text-gray-500">nobody yet</span> : null}
        {(info?.offered || []).map(o => (
          <span key={o.id} className={`px-2 py-0.5 rounded-full border ${o.claimed_at ? 'bg-orange-200 border-orange-300 text-orange-900 font-semibold' : 'bg-white border-orange-200 text-gray-700'}`}>
            {o.name}
            {o.notified_at && <span className="text-[10px] text-emerald-700 font-normal"> · texted</span>}
            {o.claimed_at && <span className="text-[10px] font-normal"> · claimed</span>}
            {!o.notified_at && !o.has_phone && <span className="text-[10px] text-amber-700 font-normal"> · no phone</span>}
            {!o.notified_at && o.has_phone && !o.texts_on && <span className="text-[10px] text-amber-700 font-normal"> · texts off</span>}
          </span>
        ))}
      </div>

      {/* Who claimed — take one off and their spot opens again */}
      {(info?.claims || []).length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
          <span className="font-semibold text-orange-900">Claimed:</span>
          {info!.claims.map(c => (
            <span key={c.id} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 rounded-full bg-white border border-orange-300 text-orange-900 font-semibold">
              {c.name}
              {/* A day: they come off every job of it (and off its route), from whichever job this is — a finished one too. */}
              {(scheduled || day) && (
                <button
                  onClick={() => {
                    // What the server does (releaseFromOpenShiftBundle_): they come off the stops still to do; the
                    // finished ones keep the people who had the day as who worked them, so nobody's pay moves.
                    const ask = day
                      ? `Take ${c.name} off ${allOfDay} on ${dayWhen}? ${dayStarted ? 'A stop is already started, so their spot does not open again.' : 'Their spot opens again.'}`
                        + (dayJobs.some(o => o.status === 'complete') ? ' They keep their pay for the stops already finished — if they did not work one, open that stop and change People on this job.' : '')
                      : `Take ${c.name} off this shift? Their spot opens again.`;
                    if (window.confirm(ask)) releaseClaim.mutate(c);
                  }}
                  disabled={busy}
                  className="text-[10px] font-normal text-gray-500 hover:text-red-700 px-1 disabled:opacity-50">
                  take off
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {/* The office already knows who's taking it (owner, Oct 22 2026: "allow me to already select someone as
          claimed"): give it to them from here — a claim by them, made from the office, that does what their own tap
          would, on every job of the shift. Filled: who has it, and "give it to someone else instead". */}
      {info && !readOnly && (scheduled || day) && !dayStarted && !editing && (
        <GiveShiftTo
          className="mt-2"
          jobId={jobId}
          claims={info.claims}
          required={required}
          filled={filled}
          disabled={busy}
          dayWords={day ? `${allOfDay} on ${dayWhen}` : null}
          onDone={(r) => {
            if (!r) { refresh(); return; }
            refresh(r);
            // Texting not set up: the messages come back for his phone, where the offer texts land too.
            setShiftDrafts(r?.texts?.drafts || []);
            setSkipped(r?.texts?.skipped || []);
          }}
        />
      )}

      {/* One job of its own: as it always was. (Never on a job of an offered day, even for the moment before its
          day has loaded or after it was just turned off — these buttons would act on that one job alone.) */}
      {!day && !onOfferedDay && scheduled && !editing && (
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs font-medium">
          <button onClick={startEdit} disabled={!info || busy} className="text-orange-800 hover:text-orange-950 disabled:opacity-50">Change who</button>
          {open ? (
            <button
              onClick={() => { if (window.confirm('Turn off the open shift? It leaves the crew\'s Open tab. Anyone who already claimed stays on it.')) saveShift.mutate({ on: false, ...sameOffer() }); }}
              disabled={!info || busy} className="text-gray-600 hover:text-red-700 disabled:opacity-50">
              Turn off open shift
            </button>
          ) : (
            <button
              onClick={() => saveShift.mutate({ on: true, ...sameOffer(), required_count: filled ? claimed + 1 : required })}
              disabled={!info || busy} className="text-orange-800 hover:text-orange-950 disabled:opacity-50"
              title={filled ? 'Opens one more spot' : 'Puts it back in the crew\'s Open tab'}>
              {filled ? 'Reopen for one more' : 'Reopen'}
            </button>
          )}
          {/* Nobody has it yet and other jobs are loose that day: put them out together instead. */}
          {claimed === 0 && offerDayLink}
        </div>
      )}

      {/* A day offered as one open shift: the same choices, for every job of it. Turning it off is always there —
          a filled or started day too — because that is how he gets to pick the crew himself: the Routes page
          leaves an offered route's crew to whoever claims it. */}
      {day && !editing && (
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs font-medium">
          {!dayStarted && (
            <button onClick={startEdit} disabled={!info || busy} className="text-orange-800 hover:text-orange-950 disabled:opacity-50">Change who</button>
          )}
          {!open && filled && !dayStarted && (
            <button
              onClick={() => saveShift.mutate({ on: true, ...sameOffer(), required_count: claimed + 1 })}
              disabled={!info || busy} className="text-orange-800 hover:text-orange-950 disabled:opacity-50"
              title={`Opens one more spot on ${allOfDay}`}>
              Reopen for one more
            </button>
          )}
          <button
            onClick={() => {
              const many = dayJobs.length > 1;
              if (window.confirm(`Turn off the open shift for ${allOfDay} on ${dayWhen}? ${many ? 'They leave' : 'It leaves'} the crew's Open tab and ${many ? 'stay together as' : 'stays on'} a route for that day${claimed > 0 ? ', with whoever claimed still on it' : ''}. After that you pick the crew on the Routes page.`)) {
                saveShift.mutate({ on: false, ...sameOffer() });
              }
            }}
            disabled={!info || busy} className="text-gray-600 hover:text-red-700 disabled:opacity-50">
            Turn off open shift
          </button>
        </div>
      )}

      {editing && <div className="mt-3 border-t border-orange-200 pt-3">{editor}</div>}

      {skipped.length > 0 && (
        <p className="mt-2 text-xs text-orange-900">Not texted: {skipped.map(k => `${k.name} (${k.why})`).join(', ')}</p>
      )}
      <OpenShiftDrafts drafts={shiftDrafts} className="mt-3 border-t border-orange-200 pt-3" />
    </div>
  );
}

function Row({ icon: Icon, label, children }: { icon: any; label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start px-4 py-3 gap-3">
      <Icon className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
        <p className="text-sm text-gray-900 mt-0.5">{children}</p>
      </div>
    </div>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <h3 className="text-base font-black uppercase tracking-tight text-gray-900">{title}</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500 hover:text-gray-600" /></button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}
